import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Snackbar,
} from '@mui/material';
import { formatDistanceToNowStrict } from 'date-fns';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArchiveIcon from '@mui/icons-material/Archive';
import DownloadIcon from '@mui/icons-material/Download';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { globalAdminAPI } from '../../services/api';
import CaseTransferManager from './CaseTransferManager';

const TAB_KEYS = ['overview', 'universities', 'case-transfer', 'billing', 'realtime', 'marking', 'compliance', 'features'];
const UNITY_ADDRESSABLE_PLATFORMS = [
  { key: 'windows', label: 'PC (Windows)' },
  { key: 'macos', label: 'Mac' },
  { key: 'ios', label: 'iOS' },
  { key: 'android', label: 'Android' },
];
const defaultAddressablesForm = UNITY_ADDRESSABLE_PLATFORMS.reduce((acc, platform) => {
  acc[platform.key] = { version: '', file: null };
  return acc;
}, {});
const defaultVoiceSamplesOverview = {
  providers: ['openai', 'gemini'],
  voices: {
    openai: [],
    gemini: [],
  },
  samples: {
    openai: [],
    gemini: [],
  },
  counts: {
    openai: 0,
    gemini: 0,
    total: 0,
  },
};
const defaultVoiceSampleForm = {
  provider: 'openai',
  voice: '',
  file: null,
};
const defaultCreateFormState = {
  name: '',
  code: '',
  slug: '',
  seatCapacity: 0,
  status: 'active',
  contactEmail: '',
  contactPhone: '',
};

const riskColor = {
  low: 'success',
  elevated: 'info',
  medium: 'warning',
  high: 'error',
  critical: 'error',
};

const defaultBillingForm = {
  freeSignupTokens: 15,
  historyCostTokens: 1,
  userRegistrationCap: 0,
  receiptsEnabled: true,
  vatInvoicesEnabled: true,
  refundPolicy: '',
  refundWindowDays: 14,
  tokenPacks: [],
};

const defaultPilotForm = {
  enabled: true,
};

const defaultRealtimeForm = {
  provider: 'openai',
  parentApiKey: '',
  realtimeModel: '',
  voice: '',
  proactiveAudio: false,
  affectiveDialog: false,
  azureEndpoint: '',
  azureRealtimeEndpoint: '',
  azureDeploymentId: '',
  azureApiVersion: '',
  azureResourceName: '',
  azureApiKey: '',
  azureUseActiveDirectory: false,
};

const defaultMarkingForm = {
  provider: 'openai',
  parentApiKey: '',
  markingModel: '',
  azureEndpoint: '',
  azureDeploymentId: '',
  azureApiVersion: '',
  azureResourceName: '',
  azureApiKey: '',
  azureUseActiveDirectory: false,
  parameters: {
    temperature: '0',
    maxOutputTokens: '',
    topP: '',
    frequencyPenalty: '',
    presencePenalty: '',
    reasoningEffort: '',
    verbosity: 'medium',
  },
};

const defaultTranscriptionForm = {
  enabled: false,
  provider: 'openai',
  parentApiKey: '',
  model: 'gpt-4o-transcribe',
  azureEndpoint: '',
  azureDeploymentId: '',
  azureApiVersion: '',
  azureResourceName: '',
  azureApiKey: '',
  azureUseActiveDirectory: false,
};

const defaultUniversityAiForm = {
  realtime: {
    ...defaultRealtimeForm,
    useGlobalCredentials: false,
  },
  marking: {
    ...defaultMarkingForm,
    useGlobalCredentials: false,
  },
  transcription: {
    ...defaultTranscriptionForm,
    useGlobalCredentials: false,
  },
};

const computeUniversityErrors = (form = {}) => {
  const errors = {};
  const name = form.name?.toString().trim();
  const code = form.code?.toString().trim();
  const slug = form.slug?.toString().trim();
  const seatCapacity = form.seatCapacity;
  const contactEmail = form.contactEmail?.toString().trim();

  if (!name) {
    errors.name = 'Name is required';
  }
  if (!code) {
    errors.code = 'Code is required';
  } else if (code.length < 2) {
    errors.code = 'Code must be at least 2 characters';
  }
  if (slug && !/^[a-z0-9-]+$/i.test(slug)) {
    errors.slug = 'Slug can only include letters, numbers, and hyphens';
  }

  if (seatCapacity !== undefined && seatCapacity !== null && seatCapacity !== '') {
    const numeric = Number(seatCapacity);
    if (Number.isNaN(numeric) || numeric < 0) {
      errors.seatCapacity = 'Seat capacity must be zero or a positive number';
    }
  }

  if (contactEmail && !/^\S+@\S+\.\S+$/.test(contactEmail)) {
    errors.contactEmail = 'Enter a valid email address';
  }

  return errors;
};

const formatCurrency = (amountCents, currency = 'GBP') => {
  if (amountCents === undefined || amountCents === null) {
    return '?';
  }
  const normalisedCurrency = (currency || 'GBP').toString().toUpperCase();
  const amount = Number(amountCents) / 100;
  if (!Number.isFinite(amount)) {
    return '?';
  }
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: normalisedCurrency,
      minimumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    return `${normalisedCurrency} ${amount.toFixed(2)}`;
  }
};

const formatTokenBalance = (tokens) => {
  const value = Number(tokens);
  if (!Number.isFinite(value)) {
    return '0';
  }
  return value.toLocaleString();
};

const formatInteger = (value) => {
  if (value === undefined || value === null) {
    return '?';
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '?';
  }
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(numeric);
};

const formatRelativeTime = (value, fallback = '?') => {
  if (!value) {
    return fallback;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return fallback;
  }
  return `${formatDistanceToNowStrict(date)} ago`;
};

const formatBytes = (value) => {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let index = 0;
  while (size >= 1024 && index < units.length - 1) {
    size /= 1024;
    index += 1;
  }
  const precision = size >= 10 || index === 0 ? 0 : 1;
  return `${size.toFixed(precision)} ${units[index]}`;
};

const ADMIN_EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

const isValidAdminEmail = (value) => ADMIN_EMAIL_PATTERN.test((value || '').toString().trim().toLowerCase());

const formatInviteExpiry = (value) => {
  if (!value) {
    return '?';
  }

  const expiryDate = new Date(value);
  if (Number.isNaN(expiryDate.getTime())) {
    return '?';
  }

  const diff = formatDistanceToNowStrict(expiryDate);
  return expiryDate.getTime() >= Date.now() ? `${diff} remaining` : `${diff} ago`;
};

const isAzureResponsesEndpoint = (value = '') => {
  const normalized = value.toString().trim();
  if (!normalized) {
    return false;
  }
  return /\/openai\/responses\/?(?:\?|$)/i.test(normalized)
    || /\/openai\/deployments\/[^/]+\/responses\/?(?:\?|$)/i.test(normalized);
};

const hasAzureApiVersion = (value = '') => /[?&]api-version=/i.test(value);

const isAzureMarkingConfigured = ({ endpoint, deploymentId, apiVersion }) => {
  const trimmedEndpoint = (endpoint || '').toString().trim();
  if (!trimmedEndpoint) {
    return false;
  }
  if (isAzureResponsesEndpoint(trimmedEndpoint)) {
    return hasAzureApiVersion(trimmedEndpoint) || Boolean((apiVersion || '').toString().trim());
  }
  return Boolean(trimmedEndpoint)
    && Boolean((deploymentId || '').toString().trim())
    && Boolean((apiVersion || '').toString().trim());
};

const isAzureTranscriptionConfigured = ({ endpoint, deploymentId, apiVersion }) => {
  const trimmedEndpoint = (endpoint || '').toString().trim();
  if (!trimmedEndpoint) {
    return false;
  }

  const isFullEndpoint = /\/openai\/(?:deployments\/[^/]+\/)?audio\/transcriptions\/?(?:\?|$)/i.test(trimmedEndpoint);
  if (isFullEndpoint) {
    return hasAzureApiVersion(trimmedEndpoint) || Boolean((apiVersion || '').toString().trim());
  }

  return Boolean((deploymentId || '').toString().trim())
    && Boolean((apiVersion || '').toString().trim());
};

const isEditableElement = (element) => {
  if (!element || typeof element !== 'object') {
    return false;
  }
  const tagName = element.tagName?.toLowerCase();
  if (tagName && ['input', 'textarea', 'select'].includes(tagName)) {
    return true;
  }
  if (element.isContentEditable) {
    return true;
  }
  const role = typeof element.getAttribute === 'function' ? element.getAttribute('role') : null;
  if (role && ['textbox', 'combobox'].includes(role)) {
    return true;
  }
  const hasPopup = typeof element.getAttribute === 'function' ? element.getAttribute('aria-haspopup') : null;
  if (hasPopup === 'listbox') {
    return true;
  }
  if (typeof element.closest === 'function') {
    if (element.closest('input, textarea, select, [contenteditable="true"]')) {
      return true;
    }
    if (element.closest('[role="textbox"], [role="combobox"], [aria-haspopup="listbox"]')) {
      return true;
    }
  }
  return false;
};

const mapFeedbackSeverity = (type) => {
  switch (type) {
    case 'error':
      return 'error';
    case 'warning':
      return 'warning';
    case 'info':
      return 'info';
    default:
      return 'success';
  }
};

const RiskChip = ({ riskLevel }) => {
  const color = riskColor[riskLevel] || 'default';
  return (
    <Chip
      size="small"
      color={color}
      label={(riskLevel || 'low').toUpperCase()}
      sx={{ fontWeight: 600 }}
    />
  );
};

const MetricCard = ({ title, value, description, icon }) => (
  <Card elevation={1}>
    <CardContent>
      <Stack direction="row" alignItems="center" spacing={2}>
        {icon}
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {value}
          </Typography>
          {description && (
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          )}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const GlobalAdminConsole = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [settings, setSettings] = useState(null);
  const [billingForm, setBillingForm] = useState(defaultBillingForm);
  const [pilotForm, setPilotForm] = useState(defaultPilotForm);
  const [realtimeForm, setRealtimeForm] = useState(defaultRealtimeForm);
  const [markingForm, setMarkingForm] = useState(defaultMarkingForm);
  const [transcriptionForm, setTranscriptionForm] = useState(defaultTranscriptionForm);
  const [featureChecklist, setFeatureChecklist] = useState([]);
  const [tenantSnapshot, setTenantSnapshot] = useState(null);
  const [tenantFilters, setTenantFilters] = useState({ status: 'all', search: '' });
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [financeOverview, setFinanceOverview] = useState(null);
  const [loadingFinance, setLoadingFinance] = useState(true);
  const [exportingFinance, setExportingFinance] = useState(false);
  const [creditInvoices, setCreditInvoices] = useState([]);
  const [creditInvoicesLoading, setCreditInvoicesLoading] = useState(false);
  const [creditInvoiceAlert, setCreditInvoiceAlert] = useState(null);
  const [creditInvoiceFilters, setCreditInvoiceFilters] = useState({ status: 'all' });
  const [creditInvoiceActions, setCreditInvoiceActions] = useState({ sending: null, markingPaid: null, downloading: null });
  const [creditInvoiceCreateForm, setCreditInvoiceCreateForm] = useState({ universityId: '', quantity: '' });
  const [creatingCreditInvoice, setCreatingCreditInvoice] = useState(false);
  const [selectedUniversityId, setSelectedUniversityId] = useState('');
  const [universityDetail, setUniversityDetail] = useState(null);
  const [universityForm, setUniversityForm] = useState(null);
  const [assignAdminForm, setAssignAdminForm] = useState({ email: '', firstName: '', lastName: '' });
  const [assigningAdmin, setAssigningAdmin] = useState(false);
  const [assignAdminFeedback, setAssignAdminFeedback] = useState(null);
  const [inviteActionState, setInviteActionState] = useState({ resendId: null, revokeId: null });
  const [loadingUniversityDetail, setLoadingUniversityDetail] = useState(false);
  const [universityAiConfig, setUniversityAiConfig] = useState(null);
  const [universityAiForm, setUniversityAiForm] = useState(defaultUniversityAiForm);
  const [universityAiLoading, setUniversityAiLoading] = useState(false);
  const [universityAiSaving, setUniversityAiSaving] = useState(false);
  const [universityAiAlert, setUniversityAiAlert] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creatingUniversity, setCreatingUniversity] = useState(false);
  const [savingUniversity, setSavingUniversity] = useState(false);
  const [savingBilling, setSavingBilling] = useState(false);
  const [savingPilot, setSavingPilot] = useState(false);
  const [savingRealtime, setSavingRealtime] = useState(false);
  const [savingMarking, setSavingMarking] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });
  const [createForm, setCreateForm] = useState(defaultCreateFormState);
  const [createFormErrors, setCreateFormErrors] = useState({});
  const [universityFormErrors, setUniversityFormErrors] = useState({});
  const [addressablesForm, setAddressablesForm] = useState(defaultAddressablesForm);
  const [addressablesUploadState, setAddressablesUploadState] = useState({});
  const [voiceSamplesOverview, setVoiceSamplesOverview] = useState(defaultVoiceSamplesOverview);
  const [voiceSampleForm, setVoiceSampleForm] = useState(defaultVoiceSampleForm);
  const [voiceSampleActionState, setVoiceSampleActionState] = useState({
    uploading: false,
    deleting: false,
    error: '',
  });

  const showToast = useCallback((message, severity = 'info') => {
    if (!message) {
      return;
    }
    setToast({ open: true, message, severity });
  }, []);

  const handleToastClose = useCallback((event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setToast((prev) => ({ ...prev, open: false }));
  }, []);

  const loadSettings = useCallback(async () => {
    setLoadingSettings(true);
    setError('');
    try {
      const [overviewResponse, featureResponse, voiceSamplesResponse] = await Promise.all([
        globalAdminAPI.getSystemSettings(),
        globalAdminAPI.getFeatureChecklist(),
        globalAdminAPI.getVoiceSamples(),
      ]);
      const overview = overviewResponse.data || overviewResponse;
      const voiceSamplesData = voiceSamplesResponse.data || voiceSamplesResponse || defaultVoiceSamplesOverview;
      setSettings(overview);
      setVoiceSamplesOverview({
        providers: Array.isArray(voiceSamplesData.providers) ? voiceSamplesData.providers : defaultVoiceSamplesOverview.providers,
        voices: {
          openai: Array.isArray(voiceSamplesData?.voices?.openai) ? voiceSamplesData.voices.openai : [],
          gemini: Array.isArray(voiceSamplesData?.voices?.gemini) ? voiceSamplesData.voices.gemini : [],
        },
        samples: {
          openai: Array.isArray(voiceSamplesData?.samples?.openai) ? voiceSamplesData.samples.openai : [],
          gemini: Array.isArray(voiceSamplesData?.samples?.gemini) ? voiceSamplesData.samples.gemini : [],
        },
        counts: {
          openai: Number(voiceSamplesData?.counts?.openai) || 0,
          gemini: Number(voiceSamplesData?.counts?.gemini) || 0,
          total: Number(voiceSamplesData?.counts?.total) || 0,
        },
      });
      setVoiceSampleForm((prev) => {
        const provider = (prev.provider === 'gemini' || prev.provider === 'openai') ? prev.provider : 'openai';
        const providerVoices = Array.isArray(voiceSamplesData?.voices?.[provider]) ? voiceSamplesData.voices[provider] : [];
        const selectedVoice = providerVoices.includes(prev.voice) ? prev.voice : (providerVoices[0] || '');
        return {
          ...prev,
          provider,
          voice: selectedVoice,
          file: null,
        };
      });
      setVoiceSampleActionState((prev) => ({ ...prev, error: '' }));
      setBillingForm({
        freeSignupTokens: overview.billing?.freeSignupTokens ?? 15,
        historyCostTokens: overview.billing?.historyCostTokens ?? 1,
        userRegistrationCap: overview.billing?.userRegistrationCap ?? 0,
        receiptsEnabled: overview.billing?.receiptsEnabled ?? true,
        vatInvoicesEnabled: overview.billing?.vatInvoicesEnabled ?? true,
        refundPolicy: overview.billing?.refundPolicy || '',
        refundWindowDays: overview.billing?.refundWindowDays ?? 14,
        tokenPacks: (overview.billing?.tokenPacks || []).map((pack) => ({
          key: pack.key,
          name: pack.name,
          tokens: pack.tokens,
          price: ((pack.priceCents || 0) / 100).toFixed(2),
          currency: (pack.currency || 'GBP').toUpperCase(),
          historiesEquivalent: pack.historiesEquivalent ?? 0,
          stripePriceId: pack.stripePriceId || '',
          isActive: pack.isActive !== false,
        })),
      });
      setPilotForm({
        enabled: typeof overview.pilot?.enabled === 'boolean'
          ? overview.pilot.enabled
          : true,
      });
      const realtimeSettings = overview.openai?.realtime || {};
      const markingSettings = overview.openai?.marking || {};
      const transcriptionSettings = overview.openai?.transcription || {};
      const mapParam = (value, fallback = '') => {
        if (value === null || value === undefined || value === '') {
          return fallback;
        }
        const numeric = Number(value);
        return Number.isFinite(numeric) ? String(numeric) : String(value);
      };
      setRealtimeForm({
        ...defaultRealtimeForm,
        provider: realtimeSettings.provider || 'openai',
        parentApiKey: '',
        realtimeModel: realtimeSettings.realtimeModel || overview.openai?.allowedRealtimeModels?.[0] || '',
        voice: realtimeSettings.voice || overview.openai?.voices?.[0] || '',
        proactiveAudio: Boolean(realtimeSettings.proactiveAudio),
        affectiveDialog: Boolean(realtimeSettings.affectiveDialog),
        azureEndpoint: realtimeSettings.azure?.endpoint || '',
        azureRealtimeEndpoint: realtimeSettings.azure?.realtimeEndpoint || '',
        azureDeploymentId: realtimeSettings.azure?.deploymentId || '',
        azureApiVersion: realtimeSettings.azure?.apiVersion || '',
        azureResourceName: realtimeSettings.azure?.resourceName || '',
        azureApiKey: '',
        azureUseActiveDirectory: Boolean(realtimeSettings.azure?.useActiveDirectory),
      });
      setMarkingForm({
        ...defaultMarkingForm,
        provider: markingSettings.provider || realtimeSettings.provider || 'openai',
        parentApiKey: '',
        markingModel: markingSettings.markingModel || overview.openai?.allowedMarkingModels?.[0] || '',
        azureEndpoint: markingSettings.azure?.endpoint || '',
        azureDeploymentId: markingSettings.azure?.deploymentId || '',
        azureApiVersion: markingSettings.azure?.apiVersion || '',
        azureResourceName: markingSettings.azure?.resourceName || '',
        azureApiKey: '',
        azureUseActiveDirectory: Boolean(markingSettings.azure?.useActiveDirectory),
        parameters: {
          temperature: mapParam(markingSettings.parameters?.temperature, defaultMarkingForm.parameters.temperature),
          maxOutputTokens: mapParam(markingSettings.parameters?.maxOutputTokens),
          topP: mapParam(markingSettings.parameters?.topP),
          frequencyPenalty: mapParam(markingSettings.parameters?.frequencyPenalty),
          presencePenalty: mapParam(markingSettings.parameters?.presencePenalty),
          reasoningEffort: markingSettings.parameters?.reasoningEffort || '',
          verbosity: markingSettings.parameters?.verbosity || defaultMarkingForm.parameters.verbosity,
        },
      });
      setTranscriptionForm({
        ...defaultTranscriptionForm,
        enabled: Boolean(transcriptionSettings.enabled),
        provider: transcriptionSettings.provider || 'openai',
        parentApiKey: '',
        model: transcriptionSettings.model || defaultTranscriptionForm.model,
        azureEndpoint: transcriptionSettings.azure?.endpoint || '',
        azureDeploymentId: transcriptionSettings.azure?.deploymentId || '',
        azureApiVersion: transcriptionSettings.azure?.apiVersion || '',
        azureResourceName: transcriptionSettings.azure?.resourceName || '',
        azureApiKey: '',
        azureUseActiveDirectory: Boolean(transcriptionSettings.azure?.useActiveDirectory),
      });
      setFeatureChecklist(featureResponse.data || featureResponse || []);
    } catch (err) {
      console.error('Failed to load global settings', err);
      setError(err?.response?.data?.message || 'Unable to load global settings');
    } finally {
      setLoadingSettings(false);
    }
  }, []);

  const loadTenants = useCallback(async () => {
    setLoadingTenants(true);
    setError('');
    try {
      const response = await globalAdminAPI.listTenants();
      setTenantSnapshot(response.data || response);
    } catch (err) {
      console.error('Failed to load tenant snapshot', err);
      setError(err?.response?.data?.message || 'Unable to load tenant snapshot');
    } finally {
      setLoadingTenants(false);
    }
  }, []);

  const loadFinance = useCallback(async () => {
    setLoadingFinance(true);
    try {
      const response = await globalAdminAPI.getFinanceOverview();
      setFinanceOverview(response.data || response);
    } catch (err) {
      console.error('Failed to load finance overview', err);
      setError((prev) => prev || err?.response?.data?.message || 'Unable to load finance overview');
    } finally {
      setLoadingFinance(false);
    }
  }, []);

  const loadCreditInvoices = useCallback(async () => {
    setCreditInvoicesLoading(true);
    setCreditInvoiceAlert(null);
    try {
      const params = {};
      if (creditInvoiceFilters.status && creditInvoiceFilters.status !== 'all') {
        params.status = creditInvoiceFilters.status;
      }
      const response = await globalAdminAPI.listCreditInvoices(params);
      setCreditInvoices(response.data?.invoices || response.invoices || []);
    } catch (err) {
      console.error('Failed to load credit invoices', err);
      setCreditInvoiceAlert({
        severity: 'error',
        message: err?.response?.data?.message || 'Unable to load credit invoices',
      });
    } finally {
      setCreditInvoicesLoading(false);
    }
  }, [creditInvoiceFilters.status]);

  const handleCreditInvoiceFilterChange = (field) => (event) => {
    setCreditInvoiceFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleAddressablesFieldChange = (platform, field) => (event) => {
    const value = field === 'file' ? event.target.files?.[0] || null : event.target.value;
    setAddressablesForm((prev) => ({
      ...prev,
      [platform]: { ...prev[platform], [field]: value },
    }));
    if (field === 'file') {
      event.target.value = null;
    }
    setAddressablesUploadState((prev) => {
      if (!prev[platform]?.error) {
        return prev;
      }
      return { ...prev, [platform]: { ...prev[platform], error: '' } };
    });
  };

  const handleUploadAddressables = async (platform) => {
    const selection = addressablesForm[platform] || {};
    if (!selection.file) {
      setAddressablesUploadState((prev) => ({
        ...prev,
        [platform]: { ...prev[platform], uploading: false, error: 'Select a .zip file to upload.' },
      }));
      return;
    }
    setAddressablesUploadState((prev) => ({
      ...prev,
      [platform]: { ...prev[platform], uploading: true, error: '' },
    }));
    try {
      await globalAdminAPI.uploadUnityAddressables({
        platform,
        version: selection.version?.trim(),
        file: selection.file,
      });
      setAddressablesForm((prev) => ({
        ...prev,
        [platform]: { ...prev[platform], file: null },
      }));
      setAddressablesUploadState((prev) => ({
        ...prev,
        [platform]: { ...prev[platform], uploading: false, error: '' },
      }));
      const platformLabel = UNITY_ADDRESSABLE_PLATFORMS.find((item) => item.key === platform)?.label || platform;
      showToast(`${platformLabel} addressables uploaded`, 'success');
      await loadSettings();
    } catch (err) {
      setAddressablesUploadState((prev) => ({
        ...prev,
        [platform]: {
          ...prev[platform],
          uploading: false,
          error: err?.response?.data?.message || 'Unable to upload addressables build.',
        },
      }));
    }
  };

  const assignAdminEmailValid = useMemo(
    () => isValidAdminEmail(assignAdminForm.email),
    [assignAdminForm.email],
  );
  const addressablesByPlatform = useMemo(() => {
    const entries = settings?.unityAddressables || [];
    return entries.reduce((acc, entry) => {
      if (entry?.platform) {
        acc[entry.platform] = entry;
      }
      return acc;
    }, {});
  }, [settings?.unityAddressables]);

  const voiceOptionsForProvider = useMemo(() => {
    const provider = voiceSampleForm.provider === 'gemini' ? 'gemini' : 'openai';
    const source = voiceSamplesOverview?.voices?.[provider];
    return Array.isArray(source) ? source : [];
  }, [voiceSamplesOverview, voiceSampleForm.provider]);

  const selectedVoiceSample = useMemo(() => {
    const provider = voiceSampleForm.provider === 'gemini' ? 'gemini' : 'openai';
    const source = voiceSamplesOverview?.samples?.[provider];
    const entries = Array.isArray(source) ? source : [];
    return entries.find((entry) => entry.voice === voiceSampleForm.voice) || null;
  }, [voiceSamplesOverview, voiceSampleForm.provider, voiceSampleForm.voice]);

  const handleVoiceSampleProviderChange = (event) => {
    const provider = event.target.value === 'gemini' ? 'gemini' : 'openai';
    const options = Array.isArray(voiceSamplesOverview?.voices?.[provider])
      ? voiceSamplesOverview.voices[provider]
      : [];
    setVoiceSampleForm((prev) => ({
      ...prev,
      provider,
      voice: options.includes(prev.voice) ? prev.voice : (options[0] || ''),
      file: null,
    }));
    setVoiceSampleActionState((prev) => ({ ...prev, error: '' }));
  };

  const handleVoiceSampleVoiceChange = (event) => {
    setVoiceSampleForm((prev) => ({ ...prev, voice: event.target.value, file: null }));
    setVoiceSampleActionState((prev) => ({ ...prev, error: '' }));
  };

  const handleVoiceSampleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setVoiceSampleForm((prev) => ({ ...prev, file }));
    event.target.value = null;
    setVoiceSampleActionState((prev) => ({ ...prev, error: '' }));
  };

  const handleUploadVoiceSample = async () => {
    if (!voiceSampleForm.provider || !voiceSampleForm.voice) {
      setVoiceSampleActionState((prev) => ({ ...prev, error: 'Choose a provider and voice.' }));
      return;
    }
    if (!voiceSampleForm.file) {
      setVoiceSampleActionState((prev) => ({ ...prev, error: 'Select an .mp3 or .wav file to upload.' }));
      return;
    }

    setVoiceSampleActionState((prev) => ({ ...prev, uploading: true, error: '' }));
    try {
      await globalAdminAPI.uploadVoiceSample({
        provider: voiceSampleForm.provider,
        voice: voiceSampleForm.voice,
        file: voiceSampleForm.file,
      });
      setVoiceSampleForm((prev) => ({ ...prev, file: null }));
      setVoiceSampleActionState((prev) => ({ ...prev, uploading: false, error: '' }));
      showToast(`Uploaded sample for ${voiceSampleForm.provider} / ${voiceSampleForm.voice}`, 'success');
      await loadSettings();
    } catch (err) {
      setVoiceSampleActionState((prev) => ({
        ...prev,
        uploading: false,
        error: err?.response?.data?.message || 'Unable to upload voice sample.',
      }));
    }
  };

  const handleDeleteVoiceSample = async () => {
    if (!voiceSampleForm.provider || !voiceSampleForm.voice) {
      setVoiceSampleActionState((prev) => ({ ...prev, error: 'Choose a provider and voice.' }));
      return;
    }

    setVoiceSampleActionState((prev) => ({ ...prev, deleting: true, error: '' }));
    try {
      await globalAdminAPI.deleteVoiceSample(voiceSampleForm.provider, voiceSampleForm.voice);
      setVoiceSampleActionState((prev) => ({ ...prev, deleting: false, error: '' }));
      showToast(`Removed sample for ${voiceSampleForm.provider} / ${voiceSampleForm.voice}`, 'success');
      await loadSettings();
    } catch (err) {
      setVoiceSampleActionState((prev) => ({
        ...prev,
        deleting: false,
        error: err?.response?.data?.message || 'Unable to remove voice sample.',
      }));
    }
  };

  const loadUniversityDetail = useCallback(async (universityId) => {
    if (!universityId) {
      setUniversityDetail(null);
      setUniversityForm(null);
      setUniversityAiConfig(null);
      setUniversityAiForm(defaultUniversityAiForm);
      setUniversityAiAlert(null);
      setAssignAdminFeedback(null);
      return null;
    }

    setLoadingUniversityDetail(true);
    setError('');

    try {
      const response = await globalAdminAPI.getUniversity(universityId);
      const detail = response.data || response;
      setUniversityDetail(detail);
      setUniversityForm({
        name: detail.name || '',
        code: detail.code || '',
        slug: detail.slug || '',
        status: detail.status || 'active',
        seatCapacity: detail.seatCapacity ?? 0,
        contactEmail: detail.contact?.email || '',
        contactPhone: detail.contact?.phone || '',
        creditUnitPrice: Number.isFinite(Number(detail.creditPricing?.unitPriceCents))
          ? (Number(detail.creditPricing?.unitPriceCents) / 100).toFixed(2)
          : '',
        creditCurrency: (detail.creditPricing?.currency || 'GBP').toUpperCase(),
        creditAllocationMode: detail.creditAllocationMode || 'assigned',
      });
      setAssignAdminForm({ email: '', firstName: '', lastName: '' });
      setInviteActionState({ resendId: null, revokeId: null });
      setUniversityFormErrors({});
      return detail;
    } catch (err) {
      console.error('Failed to load university detail', err);
      const message = err?.response?.data?.message || 'Unable to load university details';
      setError(message);
      setUniversityDetail(null);
      setUniversityForm(null);
      setAssignAdminFeedback({ type: 'error', message });
      return null;
    } finally {
      setLoadingUniversityDetail(false);
    }
  }, []);

  const loadUniversityAiConfig = useCallback(async (universityId) => {
    if (!universityId) {
      setUniversityAiConfig(null);
      setUniversityAiForm(defaultUniversityAiForm);
      return;
    }
    setUniversityAiLoading(true);
    setUniversityAiAlert(null);
    try {
      const response = await globalAdminAPI.getUniversityAiConfig(universityId);
      const data = response.data || response;
      setUniversityAiConfig(data);
      const mapParam = (value, fallback = '') => {
        if (value === null || value === undefined || value === '') {
          return fallback;
        }
        const numeric = Number(value);
        return Number.isFinite(numeric) ? String(numeric) : String(value);
      };
      const realtime = data.realtime || {};
      const marking = data.marking || {};
      const transcription = data.transcription || {};
      setUniversityAiForm({
        realtime: {
          ...defaultUniversityAiForm.realtime,
          provider: realtime.provider || 'openai',
          parentApiKey: '',
          realtimeModel: realtime.realtimeModel || data.allowedRealtimeModels?.[0] || '',
          voice: realtime.voice || (data.voices || [])[0] || '',
          proactiveAudio: Boolean(realtime.proactiveAudio),
          affectiveDialog: Boolean(realtime.affectiveDialog),
          azureEndpoint: realtime.azure?.endpoint || '',
          azureRealtimeEndpoint: realtime.azure?.realtimeEndpoint || '',
          azureDeploymentId: realtime.azure?.deploymentId || '',
          azureApiVersion: realtime.azure?.apiVersion || '',
          azureResourceName: realtime.azure?.resourceName || '',
          azureApiKey: '',
          azureUseActiveDirectory: Boolean(realtime.azure?.useActiveDirectory),
          useGlobalCredentials: Boolean(realtime.useGlobalCredentials),
        },
        marking: {
          ...defaultUniversityAiForm.marking,
          provider: marking.provider || realtime.provider || 'openai',
          parentApiKey: '',
          markingModel: marking.markingModel || data.allowedMarkingModels?.[0] || '',
          azureEndpoint: marking.azure?.endpoint || '',
          azureDeploymentId: marking.azure?.deploymentId || '',
          azureApiVersion: marking.azure?.apiVersion || '',
          azureResourceName: marking.azure?.resourceName || '',
          azureApiKey: '',
          azureUseActiveDirectory: Boolean(marking.azure?.useActiveDirectory),
          useGlobalCredentials: Boolean(marking.useGlobalCredentials),
          parameters: {
            temperature: mapParam(marking.parameters?.temperature, defaultMarkingForm.parameters.temperature),
            maxOutputTokens: mapParam(marking.parameters?.maxOutputTokens),
            topP: mapParam(marking.parameters?.topP),
            frequencyPenalty: mapParam(marking.parameters?.frequencyPenalty),
            presencePenalty: mapParam(marking.parameters?.presencePenalty),
            reasoningEffort: marking.parameters?.reasoningEffort || '',
            verbosity: marking.parameters?.verbosity || defaultMarkingForm.parameters.verbosity,
          },
        },
        transcription: {
          ...defaultUniversityAiForm.transcription,
          enabled: Boolean(transcription.enabled),
          provider: transcription.provider || 'openai',
          parentApiKey: '',
          model: transcription.model || defaultTranscriptionForm.model,
          azureEndpoint: transcription.azure?.endpoint || '',
          azureDeploymentId: transcription.azure?.deploymentId || '',
          azureApiVersion: transcription.azure?.apiVersion || '',
          azureResourceName: transcription.azure?.resourceName || '',
          azureApiKey: '',
          azureUseActiveDirectory: Boolean(transcription.azure?.useActiveDirectory),
          useGlobalCredentials: Boolean(transcription.useGlobalCredentials),
        },
      });
    } catch (err) {
      console.error('Failed to load university AI config', err);
      setUniversityAiAlert({
        severity: 'error',
        message: err?.response?.data?.message || 'Unable to load AI configuration',
      });
    } finally {
      setUniversityAiLoading(false);
    }
  }, [settings]);

  useEffect(() => {
    loadSettings();
    loadTenants();
    loadFinance();
  }, [loadSettings, loadTenants, loadFinance]);

  useEffect(() => {
    if (TAB_KEYS[activeTab] === 'billing') {
      loadCreditInvoices();
    }
  }, [activeTab, loadCreditInvoices]);

  const handleTabKeyDown = useCallback((event) => {
    if (event.defaultPrevented) {
      return;
    }
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }
    if (isEditableElement(event.target)) {
      return;
    }
    event.preventDefault();
    setActiveTab((prev) => {
      const delta = event.key === 'ArrowRight' ? 1 : -1;
      const next = prev + delta;
      if (next < 0) {
        return 0;
      }
      if (next >= TAB_KEYS.length) {
        return TAB_KEYS.length - 1;
      }
      return next;
    });
  }, []);

  const handleBillingFieldChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setBillingForm((prev) => ({ ...prev, [field]: value }));
  };

  const handlePilotFieldChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setPilotForm((prev) => ({ ...prev, [field]: value }));
  };

  const handlePackFieldChange = (index, field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setBillingForm((prev) => {
      const nextPacks = prev.tokenPacks.map((pack, idx) => (idx === index ? { ...pack, [field]: value } : pack));
      return { ...prev, tokenPacks: nextPacks };
    });
  };

  const handleAssignAdminFieldChange = (field) => (event) => {
    const value = event.target.value;
    setAssignAdminForm((prev) => ({ ...prev, [field]: value }));
    if (assignAdminFeedback?.type === 'error') {
      setAssignAdminFeedback(null);
    }
  };

  const handleSendCreditInvoice = async (invoiceId) => {
    if (!invoiceId) {
      return;
    }
    setCreditInvoiceActions((prev) => ({ ...prev, sending: invoiceId }));
    setCreditInvoiceAlert(null);
    try {
      const response = await globalAdminAPI.sendCreditInvoice(invoiceId);
      const updated = response.data || response;
      setCreditInvoices((prev) => {
        const shouldInclude = creditInvoiceFilters.status === 'all' || creditInvoiceFilters.status === updated.status;
        if (!shouldInclude) {
          return prev.filter((invoice) => invoice.id !== invoiceId);
        }
        return prev.map((invoice) => (invoice.id === invoiceId ? { ...invoice, ...updated } : invoice));
      });
      setCreditInvoiceAlert({ severity: 'success', message: `Invoice ${updated.invoiceNumber} sent successfully.` });
    } catch (err) {
      console.error('Failed to send credit invoice', err);
      setCreditInvoiceAlert({
        severity: 'error',
        message: err?.response?.data?.message || 'Unable to send credit invoice',
      });
    } finally {
      setCreditInvoiceActions((prev) => ({ ...prev, sending: null }));
    }
  };

  const handleMarkCreditInvoicePaid = async (invoiceId) => {
    if (!invoiceId) {
      return;
    }
    setCreditInvoiceActions((prev) => ({ ...prev, markingPaid: invoiceId }));
    setCreditInvoiceAlert(null);
    try {
      const response = await globalAdminAPI.markCreditInvoicePaid(invoiceId);
      const updated = response.data || response;
      setCreditInvoices((prev) => {
        const shouldInclude = creditInvoiceFilters.status === 'all' || creditInvoiceFilters.status === updated.status;
        if (!shouldInclude) {
          return prev.filter((invoice) => invoice.id !== invoiceId);
        }
        return prev.map((invoice) => (invoice.id === invoiceId ? { ...invoice, ...updated } : invoice));
      });
      setCreditInvoiceAlert({ severity: 'success', message: `Invoice ${updated.invoiceNumber} marked as paid.` });
    } catch (err) {
      console.error('Failed to mark credit invoice paid', err);
      setCreditInvoiceAlert({
        severity: 'error',
        message: err?.response?.data?.message || 'Unable to mark credit invoice paid',
      });
    } finally {
      setCreditInvoiceActions((prev) => ({ ...prev, markingPaid: null }));
    }
  };

  const handleDownloadCreditInvoicePdf = async (invoice) => {
    if (!invoice?.id) {
      return;
    }
    setCreditInvoiceActions((prev) => ({ ...prev, downloading: invoice.id }));
    setCreditInvoiceAlert(null);
    try {
      const response = await globalAdminAPI.downloadCreditInvoicePdf(invoice.id);
      const data = response.data || response;
      const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoice.invoiceNumber || invoice.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download credit invoice PDF', err);
      setCreditInvoiceAlert({
        severity: 'error',
        message: err?.response?.data?.message || 'Unable to download credit invoice PDF',
      });
    } finally {
      setCreditInvoiceActions((prev) => ({ ...prev, downloading: null }));
    }
  };

  const handleCreateCreditInvoice = async () => {
    const quantity = Number.parseInt(creditInvoiceCreateForm.quantity, 10);
    if (!creditInvoiceCreateForm.universityId) {
      setCreditInvoiceAlert({ severity: 'warning', message: 'Select a university before creating an invoice.' });
      return;
    }
    if (!Number.isInteger(quantity) || quantity <= 0) {
      setCreditInvoiceAlert({ severity: 'warning', message: 'Enter a valid credit quantity.' });
      return;
    }

    setCreatingCreditInvoice(true);
    setCreditInvoiceAlert(null);
    try {
      const response = await globalAdminAPI.createCreditInvoice({
        universityId: creditInvoiceCreateForm.universityId,
        quantity,
      });
      const created = response.data || response;
      await loadCreditInvoices();
      setCreditInvoiceCreateForm((prev) => ({ ...prev, quantity: '' }));
      setCreditInvoiceAlert({ severity: 'success', message: `Draft invoice ${created.invoiceNumber} created successfully.` });
    } catch (err) {
      console.error('Failed to create credit invoice', err);
      setCreditInvoiceAlert({
        severity: 'error',
        message: err?.response?.data?.message || 'Unable to create credit invoice',
      });
    } finally {
      setCreatingCreditInvoice(false);
    }
  };

  const submitAssignAdmin = async () => {
    if (!selectedUniversityId) {
      setAssignAdminFeedback({
        type: 'error',
        message: 'Select a university before assigning administrators.',
      });
      return;
    }

    if (!assignAdminEmailValid) {
      setAssignAdminFeedback({
        type: 'error',
        message: 'Enter a valid administrator email address.',
      });
      return;
    }

    const email = assignAdminForm.email.trim().toLowerCase();

    setAssigningAdmin(true);
    setAssignAdminFeedback(null);
    try {
      await globalAdminAPI.assignUniversityAdmin(selectedUniversityId, {
        email,
        firstName: assignAdminForm.firstName.trim(),
        lastName: assignAdminForm.lastName.trim(),
      });
      setAssignAdminFeedback({
        type: 'success',
        message: 'Administrator invite sent successfully.',
      });
      setAssignAdminForm({ email: '', firstName: '', lastName: '' });
      await loadUniversityDetail(selectedUniversityId);
    } catch (err) {
      console.error('Failed to assign university admin', err);
      const message = err?.response?.data?.message || 'Failed to assign university admin';
      setAssignAdminFeedback({ type: 'error', message });
      setError(message);
    } finally {
      setAssigningAdmin(false);
    }
  };

  const handleResendAdminInvite = async (inviteId) => {
    if (!inviteId) {
      return;
    }

    setInviteActionState((prev) => ({ ...prev, resendId: inviteId }));
    try {
      await globalAdminAPI.resendInvite(inviteId);
      setAssignAdminFeedback({ type: 'success', message: 'Invite resent successfully.' });
      await loadUniversityDetail(selectedUniversityId);
    } catch (err) {
      console.error('Failed to resend invite', err);
      const message = err?.response?.data?.message || 'Unable to resend invite';
      setAssignAdminFeedback({ type: 'error', message });
      setError(message);
    } finally {
      setInviteActionState((prev) => ({ ...prev, resendId: null }));
    }
  };

  const handleRevokeAdminInvite = async (inviteId) => {
    if (!inviteId) {
      return;
    }

    setInviteActionState((prev) => ({ ...prev, revokeId: inviteId }));
    try {
      await globalAdminAPI.revokeInvite(inviteId);
      setAssignAdminFeedback({ type: 'info', message: 'Invite revoked.' });
      await loadUniversityDetail(selectedUniversityId);
    } catch (err) {
      console.error('Failed to revoke invite', err);
      const message = err?.response?.data?.message || 'Unable to revoke invite';
      setAssignAdminFeedback({ type: 'error', message });
      setError(message);
    } finally {
      setInviteActionState((prev) => ({ ...prev, revokeId: null }));
    }
  };

  const handleCreateFieldChange = (field) => (event) => {
    const value = event.target.value;
    setCreateForm((prev) => ({ ...prev, [field]: value }));
    setCreateFormErrors((prev) => {
      if (!prev[field]) {
        return prev;
      }
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleUniversityFieldChange = (field) => (event) => {
    const value = event.target.value;
    setUniversityForm((prev) => ({ ...(prev || {}), [field]: value }));
    setUniversityFormErrors((prev) => {
      if (!prev[field]) {
        return prev;
      }
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const addTokenPack = () => {
    setBillingForm((prev) => ({
      ...prev,
      tokenPacks: [
        ...prev.tokenPacks,
        {
          key: `custom-${Date.now()}`,
          name: 'New Pack',
          tokens: 0,
          price: '0.00',
          currency: 'GBP',
          historiesEquivalent: 0,
          stripePriceId: '',
          isActive: true,
        },
      ],
    }));
  };

  const removeTokenPack = (index) => {
    setBillingForm((prev) => ({
      ...prev,
      tokenPacks: prev.tokenPacks.filter((_, idx) => idx !== index),
    }));
  };

  const openCreateUniversityDialog = () => {
    setCreateForm({ ...defaultCreateFormState });
    setCreateFormErrors({});
    setCreateDialogOpen(true);
  };

  const closeCreateUniversityDialog = () => {
    setCreateDialogOpen(false);
    setCreateForm({ ...defaultCreateFormState });
    setCreateFormErrors({});
  };

  const submitCreateUniversity = async () => {
    const validationErrors = computeUniversityErrors(createForm);
    if (Object.keys(validationErrors).length) {
      setCreateFormErrors(validationErrors);
      setError('Please correct the highlighted fields.');
      return;
    }

    setCreatingUniversity(true);
    setError('');
    try {
      const payload = {
        name: createForm.name,
        code: createForm.code,
        slug: createForm.slug,
        seatCapacity: Number.parseInt(createForm.seatCapacity, 10) || 0,
        status: createForm.status,
        contact: {
          email: createForm.contactEmail,
          phone: createForm.contactPhone,
        },
      };
      const response = await globalAdminAPI.createUniversity(payload);
      const detail = response.data || response;
      setSuccessMessage(`Created ${detail.name}`);
      setError('');
      setCreateDialogOpen(false);
      setCreateForm({ ...defaultCreateFormState });
      setCreateFormErrors({});
      await loadTenants();
      setSelectedUniversityId(detail.id);
      await loadUniversityDetail(detail.id);
    } catch (err) {
      console.error('Failed to create university', err);
      setError(err?.response?.data?.message || 'Unable to create university');
    } finally {
      setCreatingUniversity(false);
    }
  };

  const saveUniversityChanges = async () => {
    if (!selectedUniversityId || !universityForm) {
      return;
    }
    const validationErrors = computeUniversityErrors(universityForm);
    if (Object.keys(validationErrors).length) {
      setUniversityFormErrors(validationErrors);
      setError('Please correct the highlighted fields.');
      return;
    }
    setSavingUniversity(true);
    setError('');
    try {
      const creditUnitPrice = Number.parseFloat(universityForm.creditUnitPrice);
      const creditUnitPriceCents = Number.isFinite(creditUnitPrice) ? Math.round(creditUnitPrice * 100) : 0;
      const payload = {
        name: universityForm.name,
        code: universityForm.code,
        slug: universityForm.slug,
        status: universityForm.status,
        seatCapacity: Number.parseInt(universityForm.seatCapacity, 10) || 0,
        contact: {
          email: universityForm.contactEmail,
          phone: universityForm.contactPhone,
        },
        creditUnitPriceCents,
        creditCurrency: (universityForm.creditCurrency || 'GBP').toUpperCase(),
        creditAllocationMode: universityForm.creditAllocationMode || 'assigned',
      };
      const response = await globalAdminAPI.updateUniversity(selectedUniversityId, payload);
      const detail = response.data || response;
      setUniversityDetail(detail);
      setUniversityForm({
        name: detail.name || '',
        code: detail.code || '',
        slug: detail.slug || '',
        status: detail.status || 'active',
        seatCapacity: detail.seatCapacity ?? 0,
        contactEmail: detail.contact?.email || '',
        contactPhone: detail.contact?.phone || '',
        creditUnitPrice: Number.isFinite(Number(detail.creditPricing?.unitPriceCents))
          ? (Number(detail.creditPricing?.unitPriceCents) / 100).toFixed(2)
          : '',
        creditCurrency: (detail.creditPricing?.currency || 'GBP').toUpperCase(),
        creditAllocationMode: detail.creditAllocationMode || 'assigned',
      });
      setUniversityFormErrors({});
      setSuccessMessage(`Updated ${detail.name}`);
      setError('');
      await loadTenants();
    } catch (err) {
      console.error('Failed to update university', err);
      setError(err?.response?.data?.message || 'Unable to update university');
    } finally {
      setSavingUniversity(false);
    }
  };

  const archiveSelectedUniversity = async () => {
    if (!selectedUniversityId) {
      return;
    }
    const reason = window.prompt('Provide a reason for archiving this university (optional):', '');
    if (reason === null) {
      return;
    }
    setSavingUniversity(true);
    setError('');
    try {
      const response = await globalAdminAPI.archiveUniversity(selectedUniversityId, { reason });
      const detail = response.data || response;
      setUniversityDetail(detail);
      setUniversityForm({
        name: detail.name || '',
        code: detail.code || '',
        slug: detail.slug || '',
        status: detail.status || 'archived',
        seatCapacity: detail.seatCapacity ?? 0,
        contactEmail: detail.contact?.email || '',
        contactPhone: detail.contact?.phone || '',
        creditUnitPrice: Number.isFinite(Number(detail.creditPricing?.unitPriceCents))
          ? (Number(detail.creditPricing?.unitPriceCents) / 100).toFixed(2)
          : '',
        creditCurrency: (detail.creditPricing?.currency || 'GBP').toUpperCase(),
        creditAllocationMode: detail.creditAllocationMode || 'assigned',
      });
      setUniversityFormErrors({});
      setSuccessMessage('');
      setError('');
      showToast(`Archived ${detail.name}`, 'success');
      await loadTenants();
    } catch (err) {
      console.error('Failed to archive university', err);
      setError(err?.response?.data?.message || 'Unable to archive university');
      showToast(err?.response?.data?.message || 'Unable to archive university', 'error');
    } finally {
      setSavingUniversity(false);
    }
  };

  const handleSelectUniversity = async (universityId) => {
    setSelectedUniversityId(universityId);
    setUniversityFormErrors({});
    setAssignAdminFeedback(null);
    await loadUniversityDetail(universityId);
    await loadUniversityAiConfig(universityId);
  };

  const handleExportFinanceReport = async () => {
    setExportingFinance(true);
    try {
      const response = await globalAdminAPI.exportFinanceReport();
      const data = response.data || response;
      const blob = data instanceof Blob ? data : new Blob([data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'finance-report.csv';
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export finance report', err);
      setError(err?.response?.data?.message || 'Unable to export finance report');
    } finally {
      setExportingFinance(false);
    }
  };

  const updateUniversityRealtimeField = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setUniversityAiForm((prev) => ({
      ...prev,
      realtime: {
        ...(prev.realtime || defaultUniversityAiForm.realtime),
        [field]: value,
      },
    }));
  };

  const updateUniversityMarkingField = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setUniversityAiForm((prev) => ({
      ...prev,
      marking: {
        ...(prev.marking || defaultUniversityAiForm.marking),
        [field]: value,
      },
    }));
  };

  const updateUniversityTranscriptionField = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setUniversityAiForm((prev) => ({
      ...prev,
      transcription: {
        ...(prev.transcription || defaultUniversityAiForm.transcription),
        [field]: value,
      },
    }));
  };

  const updateMarkingParameterField = (field) => (event) => {
    const value = event.target.value;
    setUniversityAiForm((prev) => ({
      ...prev,
      marking: {
        ...(prev.marking || defaultUniversityAiForm.marking),
        parameters: {
          ...(prev.marking?.parameters || defaultMarkingForm.parameters),
          [field]: value,
        },
      },
    }));
  };

  const saveUniversityAiConfig = async () => {
    if (!selectedUniversityId) {
      return;
    }
    const normalizeNumber = (value) => {
      if (value === null || value === undefined || value === '') {
        return undefined;
      }
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : undefined;
    };
    setUniversityAiSaving(true);
    setUniversityAiAlert(null);
    try {
      const realtime = universityAiForm.realtime || defaultUniversityAiForm.realtime;
      const marking = universityAiForm.marking || defaultUniversityAiForm.marking;
      const transcription = universityAiForm.transcription || defaultUniversityAiForm.transcription;
      const realtimeModel = realtime.realtimeModel?.trim();
      const markingModel = marking.markingModel?.trim();
      const transcriptionModel = transcription.model?.trim();
      if (!realtimeModel || !markingModel) {
        setUniversityAiAlert({
          severity: 'warning',
          message: 'Realtime and marking model names are required.',
        });
        return;
      }
      const hasRealtimeEndpoint = Boolean(realtime.azureRealtimeEndpoint?.trim());
      if (!realtime.useGlobalCredentials && realtime.provider === 'azure' && !hasRealtimeEndpoint && (!realtime.azureEndpoint?.trim() || !realtime.azureDeploymentId?.trim() || !realtime.azureApiVersion?.trim())) {
        setUniversityAiAlert({
          severity: 'warning',
          message: 'Provide either a full Azure realtime endpoint URL or endpoint + deployment id + API version.',
        });
        return;
      }
      const markingEndpointConfigured = isAzureMarkingConfigured({
        endpoint: marking.azureEndpoint,
        deploymentId: marking.azureDeploymentId,
        apiVersion: marking.azureApiVersion,
      });
      if (!marking.useGlobalCredentials && marking.provider === 'azure' && !markingEndpointConfigured) {
        setUniversityAiAlert({
          severity: 'warning',
          message: 'Provide either a full Azure responses endpoint URL (with api-version) or endpoint + deployment id + API version for marking.',
        });
        return;
      }
      if (transcription.enabled && !transcriptionModel) {
        setUniversityAiAlert({
          severity: 'warning',
          message: 'Transcription model name is required when transcription override is enabled.',
        });
        return;
      }
      const transcriptionEndpointConfigured = isAzureTranscriptionConfigured({
        endpoint: transcription.azureEndpoint,
        deploymentId: transcription.azureDeploymentId,
        apiVersion: transcription.azureApiVersion,
      });
      if (
        transcription.enabled
        && !transcription.useGlobalCredentials
        && transcription.provider === 'azure'
        && !transcriptionEndpointConfigured
      ) {
        setUniversityAiAlert({
          severity: 'warning',
          message: 'Provide either a full Azure audio transcriptions endpoint URL (with api-version) or endpoint + deployment id + API version for transcription.',
        });
        return;
      }
        await globalAdminAPI.updateUniversityAiConfig(selectedUniversityId, {
          realtime: {
            provider: realtime.provider || 'openai',
            parentApiKey: realtime.parentApiKey,
            realtimeModel,
            voice: realtime.voice,
            proactiveAudio: Boolean(realtime.proactiveAudio),
            affectiveDialog: Boolean(realtime.affectiveDialog),
            azureEndpoint: realtime.azureEndpoint,
          azureRealtimeEndpoint: realtime.azureRealtimeEndpoint,
          azureDeploymentId: realtime.azureDeploymentId,
          azureApiVersion: realtime.azureApiVersion,
          azureResourceName: realtime.azureResourceName,
          azureApiKey: realtime.azureApiKey,
          azureUseActiveDirectory: realtime.azureUseActiveDirectory,
          useGlobalCredentials: Boolean(realtime.useGlobalCredentials),
        },
        marking: {
          provider: marking.provider || realtime.provider || 'openai',
          parentApiKey: marking.parentApiKey,
          markingModel,
          azureEndpoint: marking.azureEndpoint,
          azureDeploymentId: marking.azureDeploymentId,
          azureApiVersion: marking.azureApiVersion,
          azureResourceName: marking.azureResourceName,
          azureApiKey: marking.azureApiKey,
          azureUseActiveDirectory: marking.azureUseActiveDirectory,
          useGlobalCredentials: Boolean(marking.useGlobalCredentials),
          parameters: {
            temperature: normalizeNumber(marking.parameters?.temperature),
            maxOutputTokens: normalizeNumber(marking.parameters?.maxOutputTokens),
            topP: normalizeNumber(marking.parameters?.topP),
            frequencyPenalty: normalizeNumber(marking.parameters?.frequencyPenalty),
            presencePenalty: normalizeNumber(marking.parameters?.presencePenalty),
            reasoningEffort: marking.parameters?.reasoningEffort?.trim() || undefined,
            verbosity: marking.parameters?.verbosity?.trim() || defaultMarkingForm.parameters.verbosity,
          },
        },
        transcription: {
          enabled: Boolean(transcription.enabled),
          provider: transcription.provider || 'openai',
          parentApiKey: transcription.parentApiKey,
          model: transcriptionModel || defaultTranscriptionForm.model,
          azureEndpoint: transcription.azureEndpoint,
          azureDeploymentId: transcription.azureDeploymentId,
          azureApiVersion: transcription.azureApiVersion,
          azureResourceName: transcription.azureResourceName,
          azureApiKey: transcription.azureApiKey,
          azureUseActiveDirectory: transcription.azureUseActiveDirectory,
          useGlobalCredentials: Boolean(transcription.useGlobalCredentials),
        },
      });
      setUniversityAiAlert({ severity: 'success', message: 'AI configuration updated successfully' });
      setUniversityAiForm((prev) => ({
        realtime: { ...(prev.realtime || {}), parentApiKey: '', azureApiKey: '' },
        marking: { ...(prev.marking || {}), parentApiKey: '', azureApiKey: '' },
        transcription: { ...(prev.transcription || {}), parentApiKey: '', azureApiKey: '' },
      }));
      await loadUniversityAiConfig(selectedUniversityId);
    } catch (err) {
      setUniversityAiAlert({
        severity: 'error',
        message: err?.response?.data?.message || 'Failed to update AI configuration',
      });
    } finally {
      setUniversityAiSaving(false);
    }
  };

  const savePilotSettings = async () => {
    setSavingPilot(true);
    setSuccessMessage('');
    setError('');
    try {
      await globalAdminAPI.updatePilotSettings({
        enabled: Boolean(pilotForm.enabled),
      });
      setSuccessMessage('Pilot mode updated successfully');
      await loadSettings();
    } catch (err) {
      console.error('Failed to update pilot settings', err);
      setError(err?.response?.data?.message || 'Unable to update pilot mode');
    } finally {
      setSavingPilot(false);
    }
  };

  const saveBillingSettings = async () => {
    setSavingBilling(true);
    setSuccessMessage('');
    setError('');
    try {
      const payload = {
        freeSignupTokens: Number.parseInt(billingForm.freeSignupTokens, 10) || 0,
        historyCostTokens: Number.parseInt(billingForm.historyCostTokens, 10) || 0,
        userRegistrationCap: Number.parseInt(billingForm.userRegistrationCap, 10) || 0,
        receiptsEnabled: Boolean(billingForm.receiptsEnabled),
        vatInvoicesEnabled: Boolean(billingForm.vatInvoicesEnabled),
        refundPolicy: billingForm.refundPolicy,
        refundWindowDays: Number.parseInt(billingForm.refundWindowDays, 10) || 0,
        tokenPacks: billingForm.tokenPacks.map((pack) => ({
          key: pack.key,
          name: pack.name,
          tokens: Number.parseInt(pack.tokens, 10) || 0,
          priceCents: Math.round((Number.parseFloat(pack.price) || 0) * 100),
          currency: (pack.currency || 'GBP').toLowerCase(),
          historiesEquivalent: Number.parseInt(pack.historiesEquivalent, 10) || 0,
          stripePriceId: pack.stripePriceId || '',
          isActive: Boolean(pack.isActive),
        })),
      };
      await globalAdminAPI.updateBillingSettings(payload);
      setSuccessMessage('Billing settings updated successfully');
      await loadSettings();
    } catch (err) {
      console.error('Failed to update billing settings', err);
      setError(err?.response?.data?.message || 'Unable to update billing settings');
    } finally {
      setSavingBilling(false);
    }
  };

  const saveOpenAiSettings = async (target = 'realtime') => {
    if (target === 'marking') {
      setSavingMarking(true);
    } else {
      setSavingRealtime(true);
    }
    setSuccessMessage('');
    setError('');
    const normalizeNumber = (value) => {
      if (value === null || value === undefined || value === '') {
        return undefined;
      }
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : undefined;
    };
      try {
        const realtimeModel = realtimeForm.realtimeModel?.trim();
        const markingModel = markingForm.markingModel?.trim();
        const transcriptionModel = transcriptionForm.model?.trim();
        if (!realtimeModel) {
          setError('Realtime model name is required.');
          return;
        }
      if (!markingModel) {
        setError('Marking model name is required.');
        return;
      }
      const hasRealtimeEndpoint = Boolean(realtimeForm.azureRealtimeEndpoint?.trim());
      if (realtimeForm.provider === 'azure' && !hasRealtimeEndpoint && (!realtimeForm.azureEndpoint?.trim() || !realtimeForm.azureDeploymentId?.trim() || !realtimeForm.azureApiVersion?.trim())) {
        setError('Provide either a full Azure realtime endpoint URL or endpoint + deployment id + API version.');
        return;
      }
      const markingEndpointConfigured = isAzureMarkingConfigured({
        endpoint: markingForm.azureEndpoint,
        deploymentId: markingForm.azureDeploymentId,
        apiVersion: markingForm.azureApiVersion,
      });
      if (markingForm.provider === 'azure' && !markingEndpointConfigured) {
        setError('Provide either a full Azure responses endpoint URL (with api-version) or endpoint + deployment id + API version for marking.');
        return;
      }
      if (transcriptionForm.enabled && !transcriptionModel) {
        setError('Transcription model name is required when override is enabled.');
        return;
      }
      const transcriptionEndpointConfigured = isAzureTranscriptionConfigured({
        endpoint: transcriptionForm.azureEndpoint,
        deploymentId: transcriptionForm.azureDeploymentId,
        apiVersion: transcriptionForm.azureApiVersion,
      });
      if (transcriptionForm.enabled && transcriptionForm.provider === 'azure' && !transcriptionEndpointConfigured) {
        setError('Provide either a full Azure audio transcriptions endpoint URL (with api-version) or endpoint + deployment id + API version for transcription.');
        return;
      }

        await globalAdminAPI.updateOpenAiSettings({
          realtime: {
            provider: realtimeForm.provider || 'openai',
            parentApiKey: realtimeForm.parentApiKey,
            realtimeModel,
            voice: realtimeForm.voice,
            proactiveAudio: Boolean(realtimeForm.proactiveAudio),
            affectiveDialog: Boolean(realtimeForm.affectiveDialog),
            azureEndpoint: realtimeForm.azureEndpoint,
          azureRealtimeEndpoint: realtimeForm.azureRealtimeEndpoint,
          azureDeploymentId: realtimeForm.azureDeploymentId,
          azureApiVersion: realtimeForm.azureApiVersion,
          azureResourceName: realtimeForm.azureResourceName,
          azureApiKey: realtimeForm.azureApiKey,
          azureUseActiveDirectory: realtimeForm.azureUseActiveDirectory,
        },
        marking: {
          provider: markingForm.provider || realtimeForm.provider || 'openai',
          parentApiKey: markingForm.parentApiKey,
          markingModel,
          azureEndpoint: markingForm.azureEndpoint,
          azureDeploymentId: markingForm.azureDeploymentId,
          azureApiVersion: markingForm.azureApiVersion,
          azureResourceName: markingForm.azureResourceName,
          azureApiKey: markingForm.azureApiKey,
          azureUseActiveDirectory: markingForm.azureUseActiveDirectory,
          parameters: {
            temperature: normalizeNumber(markingForm.parameters?.temperature),
            maxOutputTokens: normalizeNumber(markingForm.parameters?.maxOutputTokens),
            topP: normalizeNumber(markingForm.parameters?.topP),
            frequencyPenalty: normalizeNumber(markingForm.parameters?.frequencyPenalty),
            presencePenalty: normalizeNumber(markingForm.parameters?.presencePenalty),
            reasoningEffort: markingForm.parameters?.reasoningEffort?.trim() || undefined,
            verbosity: markingForm.parameters?.verbosity?.trim() || 'medium',
          },
        },
        transcription: {
          enabled: Boolean(transcriptionForm.enabled),
          provider: transcriptionForm.provider || 'openai',
          parentApiKey: transcriptionForm.parentApiKey,
          model: transcriptionModel || defaultTranscriptionForm.model,
          azureEndpoint: transcriptionForm.azureEndpoint,
          azureDeploymentId: transcriptionForm.azureDeploymentId,
          azureApiVersion: transcriptionForm.azureApiVersion,
          azureResourceName: transcriptionForm.azureResourceName,
          azureApiKey: transcriptionForm.azureApiKey,
          azureUseActiveDirectory: transcriptionForm.azureUseActiveDirectory,
        },
      });
      setSuccessMessage('OpenAI defaults updated successfully');
      setRealtimeForm((prev) => ({ ...prev, parentApiKey: '', azureApiKey: '' }));
      setMarkingForm((prev) => ({ ...prev, parentApiKey: '', azureApiKey: '' }));
      setTranscriptionForm((prev) => ({ ...prev, parentApiKey: '', azureApiKey: '' }));
      await loadSettings();
    } catch (err) {
      console.error('Failed to update OpenAI settings', err);
      setError(err?.response?.data?.message || 'Unable to update OpenAI settings');
    } finally {
      setSavingRealtime(false);
      setSavingMarking(false);
    }
  };

  const realtimeTenantForm = universityAiForm.realtime || defaultUniversityAiForm.realtime;
  const markingTenantForm = universityAiForm.marking || defaultUniversityAiForm.marking;
  const transcriptionTenantForm = universityAiForm.transcription || defaultUniversityAiForm.transcription;

  const filteredTenants = useMemo(() => {
    if (!tenantSnapshot?.tenants) {
      return [];
    }
    return tenantSnapshot.tenants.filter((tenant) => {
      const matchesStatus = tenantFilters.status === 'all' || tenant.status === tenantFilters.status;
      const matchesSearch = tenantFilters.search
        ? tenant.name.toLowerCase().includes(tenantFilters.search.toLowerCase())
          || tenant.code?.toLowerCase()?.includes(tenantFilters.search.toLowerCase())
        : true;
    return matchesStatus && matchesSearch;
  });
  }, [tenantSnapshot, tenantFilters]);

  const isCreateFormValid = useMemo(
    () => Object.keys(computeUniversityErrors(createForm)).length === 0,
    [createForm],
  );

  const isUniversityFormValid = useMemo(() => {
    if (!universityForm) {
      return false;
    }
    return Object.keys(computeUniversityErrors(universityForm)).length === 0;
  }, [universityForm]);

  const renderOverviewTab = () => {
    if (loadingSettings || !settings) {
      return (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>Loading overview...</Typography>
        </Box>
      );
    }

    const userLimit = settings.counts?.userLimit;
    const limitDescription = userLimit
      ? `${settings.counts?.users || 0} of ${userLimit} seats used`
      : `${settings.counts?.users || 0} users active`;

    return (
      <Stack spacing={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <MetricCard
              title="Registered users"
              value={formatInteger(settings.counts?.users)}
              description={limitDescription}
              icon={<CheckCircleIcon color="primary" />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <MetricCard
              title="Complimentary tokens"
              value={formatTokenBalance(settings.billing?.freeSignupTokens ?? 0)}
              description="Granted on new individual sign-up"
              icon={<CheckCircleIcon color="success" />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <MetricCard
              title="Tokens per history"
              value={formatTokenBalance(settings.billing?.historyCostTokens ?? 1)}
              description="Deducted whenever a history is submitted"
              icon={<WarningAmberIcon color="warning" />}
            />
          </Grid>
        </Grid>

        <Paper elevation={0} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Recent activity</Typography>
          {loadingTenants ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Stack spacing={2}>
              {(tenantSnapshot?.tenants || []).slice(0, 5).map((tenant) => (
                <Stack key={tenant.id} direction="row" spacing={2} alignItems="center">
                  <Typography sx={{ fontWeight: 600, minWidth: 220 }}>{tenant.name}</Typography>
                  <RiskChip riskLevel={tenant.riskLevel} />
                  <Typography variant="body2" color="text.secondary">
                    Wallet balance: {formatTokenBalance(tenant.walletBalance ?? 0)} credits
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Last activity: {tenant.history?.lastActivity
                      ? `${formatDistanceToNowStrict(new Date(tenant.history.lastActivity))} ago`
                      : 'No submissions'}
                  </Typography>
                </Stack>
              ))}
              {!(tenantSnapshot?.tenants || []).length && (
                <Typography variant="body2" color="text.secondary">
                  No tenants found yet. Create your first university to get started.
                </Typography>
              )}
            </Stack>
          )}
        </Paper>
      </Stack>
    );
  };

  const renderUniversitiesTab = () => (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
        <TextField
          label="Search tenants"
          value={tenantFilters.search}
          onChange={(event) => setTenantFilters((prev) => ({ ...prev, search: event.target.value }))}
          sx={{ maxWidth: 320 }}
        />
        <TextField
          select
          label="Status"
          value={tenantFilters.status}
          onChange={(event) => setTenantFilters((prev) => ({ ...prev, status: event.target.value }))}
          sx={{ maxWidth: 200 }}
        >
          <MenuItem value="all">All statuses</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
          <MenuItem value="archived">Archived</MenuItem>
        </TextField>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          onClick={openCreateUniversityDialog}
        >
          New university
        </Button>
      </Stack>

      <Paper elevation={0} sx={{ p: 2 }}>
        {loadingTenants ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>University</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Credit balance</TableCell>
                <TableCell>Activity</TableCell>
                <TableCell>Risk</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
                {filteredTenants.map((tenant) => {
                  const lastActivity = tenant.history?.lastActivity || tenant.activity?.lastHistoryAt;
                  return (
                  <TableRow
                    key={tenant.id}
                    hover
                    selected={selectedUniversityId === tenant.id}
                    onClick={() => handleSelectUniversity(tenant.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="subtitle2">{tenant.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{tenant.code}</Typography>
                    </TableCell>
                    <TableCell>{tenant.status}</TableCell>
                    <TableCell>{`${formatTokenBalance(tenant.walletBalance ?? tenant.wallet?.balance ?? 0)} credits`}</TableCell>
                    <TableCell>{formatRelativeTime(lastActivity, 'No data')}</TableCell>
                    <TableCell><RiskChip riskLevel={tenant.riskLevel} /></TableCell>
                  </TableRow>
                );
                })}
              {!filteredTenants.length && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">No universities match your filters.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Paper elevation={0} sx={{ p: 3 }}>
        {loadingUniversityDetail ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : !selectedUniversityId ? (
          <Typography variant="body2" color="text.secondary">
            Select a university from the table to view and edit its configuration.
          </Typography>
        ) : !universityDetail ? (
          <Typography variant="body2" color="text.secondary">
            Unable to load the selected university. Please try again.
          </Typography>
        ) : (
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
              <Box>
                <Typography variant="h6">{universityDetail.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Code {universityDetail.code} | Status {universityDetail.status}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Users: {universityDetail.counts?.users ?? 0}
                </Typography>
              </Box>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <Button
                    variant="contained"
                    onClick={saveUniversityChanges}
                    disabled={savingUniversity || !universityForm || !isUniversityFormValid}
                  >
                    {savingUniversity ? 'Saving...' : 'Save changes'}
                  </Button>
                <Tooltip title={universityDetail.status === 'archived' ? 'University already archived' : 'Archive university'}>
                  <span>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<ArchiveIcon />}
                      onClick={archiveSelectedUniversity}
                      disabled={savingUniversity || universityDetail.status === 'archived'}
                    >
                      Archive
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Name"
                  value={universityForm?.name || ''}
                  onChange={handleUniversityFieldChange('name')}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Code"
                  value={universityForm?.code || ''}
                  onChange={handleUniversityFieldChange('code')}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Slug"
                  value={universityForm?.slug || ''}
                  onChange={handleUniversityFieldChange('slug')}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  label="Status"
                  value={universityForm?.status || 'active'}
                  onChange={handleUniversityFieldChange('status')}
                  fullWidth
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="archived">Archived</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Seat capacity"
                  type="number"
                  value={universityForm?.seatCapacity ?? 0}
                  onChange={handleUniversityFieldChange('seatCapacity')}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Contact email"
                  value={universityForm?.contactEmail || ''}
                  onChange={handleUniversityFieldChange('contactEmail')}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Contact phone"
                  value={universityForm?.contactPhone || ''}
                  onChange={handleUniversityFieldChange('contactPhone')}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Paper elevation={0} sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Typography variant="subtitle1">Credit billing</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Credit price per history"
                      type="number"
                      value={universityForm?.creditUnitPrice || ''}
                      onChange={handleUniversityFieldChange('creditUnitPrice')}
                      helperText="Set the university-specific price (e.g., 0.40)"
                      inputProps={{ step: 0.01, min: 0 }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      label="Currency"
                      value={universityForm?.creditCurrency || 'GBP'}
                      onChange={handleUniversityFieldChange('creditCurrency')}
                      fullWidth
                    >
                      <MenuItem value="GBP">GBP</MenuItem>
                      <MenuItem value="USD">USD</MenuItem>
                      <MenuItem value="EUR">EUR</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      label="Allocation mode"
                      value={universityForm?.creditAllocationMode || 'assigned'}
                      onChange={handleUniversityFieldChange('creditAllocationMode')}
                      fullWidth
                    >
                      <MenuItem value="assigned">Assigned credits (per-student wallets)</MenuItem>
                      <MenuItem value="shared_pool">Shared pool (students draw from central balance)</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </Stack>
            </Paper>

            <Paper elevation={0} sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Typography variant="subtitle1">Administrators</Typography>
                {Array.isArray(universityDetail.administrators) && universityDetail.administrators.length ? (
                  <Stack spacing={1}>
                    {universityDetail.administrators.map((admin) => (
                      <Box
                        key={admin.id || admin.email}
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', md: 'row' },
                          justifyContent: 'space-between',
                          alignItems: { xs: 'flex-start', md: 'center' },
                          gap: 1,
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle2">{[admin.firstName, admin.lastName].filter(Boolean).join(' ') || admin.email}</Typography>
                          <Typography variant="body2" color="text.secondary">{admin.email}</Typography>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip size="small" label={admin.legacyRole || admin.role || 'admin'} />
                          <Typography variant="caption" color="text.secondary">
                            Last login: {admin.lastLoginAt ? `${formatDistanceToNowStrict(new Date(admin.lastLoginAt))} ago` : 'Never'}
                          </Typography>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">No administrators assigned yet.</Typography>
                )}
              </Stack>
            </Paper>

            <Paper elevation={0} sx={{ p: 2 }}>
              <Stack spacing={2}>
                {assignAdminFeedback && (
                  <Alert
                    severity={mapFeedbackSeverity(assignAdminFeedback.type)}
                    onClose={() => setAssignAdminFeedback(null)}
                  >
                    {assignAdminFeedback.message}
                  </Alert>
                )}
                <Typography variant="subtitle1">Invite an administrator</Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-end' }}>
                  <TextField
                    label="Admin email"
                    value={assignAdminForm.email}
                    onChange={handleAssignAdminFieldChange('email')}
                    type="email"
                    required
                    sx={{ flex: 1 }}
                    error={Boolean(assignAdminForm.email) && !assignAdminEmailValid}
                    helperText={assignAdminForm.email && !assignAdminEmailValid ? 'Enter a valid email address' : undefined}
                  />
                  <TextField
                    label="First name (optional)"
                    value={assignAdminForm.firstName}
                    onChange={handleAssignAdminFieldChange('firstName')}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Last name (optional)"
                    value={assignAdminForm.lastName}
                    onChange={handleAssignAdminFieldChange('lastName')}
                    sx={{ flex: 1 }}
                  />
                  <Button
                    variant="contained"
                    onClick={submitAssignAdmin}
                    disabled={assigningAdmin || !assignAdminEmailValid}
                  >
                    {assigningAdmin ? 'Sending...' : 'Send invite'}
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            <Paper elevation={0} sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Typography variant="subtitle1">Pending admin invites</Typography>
                {Array.isArray(universityDetail.adminInvites) && universityDetail.adminInvites.length ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Email</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Expires</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {universityDetail.adminInvites.map((invite) => (
                        <TableRow key={invite.id || invite.email}>
                          <TableCell>{invite.email}</TableCell>
                          <TableCell>{invite.status}</TableCell>
                          <TableCell>{formatInviteExpiry(invite.expiresAt)}</TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => handleResendAdminInvite(invite.id)}
                                disabled={inviteActionState.resendId === invite.id}
                              >
                                {inviteActionState.resendId === invite.id ? 'Resending...' : 'Resend'}
                              </Button>
                              <Button
                                size="small"
                                variant="text"
                                color="error"
                                onClick={() => handleRevokeAdminInvite(invite.id)}
                                disabled={inviteActionState.revokeId === invite.id}
                              >
                                {inviteActionState.revokeId === invite.id ? 'Revoking...' : 'Revoke'}
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" color="text.secondary">No pending admin invites.</Typography>
                )}
              </Stack>
            </Paper>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <MetricCard
                title="Credit balance"
                value={`${formatTokenBalance(universityDetail.wallet?.balance ?? 0)} credits`}
                description={`Last top-up ${universityDetail.wallet?.lastTopUpAt ? formatDistanceToNowStrict(new Date(universityDetail.wallet.lastTopUpAt)) + ' ago' : 'N/A'}`}
                icon={<CheckCircleIcon color="success" />}
              />
              <MetricCard
                title="Recent history"
                value={universityDetail.activity?.lastHistoryAt
                  ? formatDistanceToNowStrict(new Date(universityDetail.activity.lastHistoryAt)) + ' ago'
                  : 'No submissions'}
                description="Most recent student submission"
                icon={<WarningAmberIcon color="warning" />}
              />
            </Stack>

            <Paper elevation={0} sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Typography variant="subtitle1">AI configuration</Typography>
                {universityAiAlert && (
                  <Alert severity={universityAiAlert.severity} onClose={() => setUniversityAiAlert(null)}>
                    {universityAiAlert.message}
                  </Alert>
                )}
                {universityAiLoading ? (
                  <Box sx={{ py: 3, textAlign: 'center' }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Stack spacing={3}>
                    <Paper elevation={0} sx={{ p: 2 }}>
                      <Stack spacing={2}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                          <TextField
                            select
                            label="Realtime provider"
                            value={realtimeTenantForm.provider}
                            onChange={updateUniversityRealtimeField('provider')}
                            sx={{ minWidth: 200 }}
                            disabled={Boolean(realtimeTenantForm.useGlobalCredentials)}
                          >
                            <MenuItem value="openai">OpenAI</MenuItem>
                            <MenuItem value="azure">Azure OpenAI</MenuItem>
                            <MenuItem value="gemini">Gemini Live</MenuItem>
                          </TextField>
                          <FormControlLabel
                            control={(
                              <Switch
                                checked={Boolean(realtimeTenantForm.useGlobalCredentials)}
                                onChange={(event) => setUniversityAiForm((prev) => ({
                                  ...prev,
                                  realtime: {
                                    ...(prev.realtime || defaultUniversityAiForm.realtime),
                                    useGlobalCredentials: event.target.checked,
                                  },
                                }))}
                              />
                          )}
                          label="Use global credentials"
                        />
                      </Stack>
                      {realtimeTenantForm.provider === 'openai' && (
                        <TextField
                          label="Parent OpenAI API key"
                            type="password"
                            value={realtimeTenantForm.parentApiKey}
                            onChange={updateUniversityRealtimeField('parentApiKey')}
                            disabled={Boolean(realtimeTenantForm.useGlobalCredentials)}
                            helperText={
                              realtimeTenantForm.useGlobalCredentials
                                ? 'Using global OpenAI key'
                                : (universityAiConfig?.realtime?.openaiKeyConfigured
                                  ? 'Leave blank to keep the existing key'
                                  : 'Provide your institution API key')
                            }
                          />
                        )}
                        {realtimeTenantForm.provider === 'gemini' && (
                          <Stack spacing={2}>
                            <TextField
                              label="Parent Gemini API key"
                              type="password"
                              value={realtimeTenantForm.parentApiKey}
                              onChange={updateUniversityRealtimeField('parentApiKey')}
                              disabled={Boolean(realtimeTenantForm.useGlobalCredentials)}
                              helperText={
                                realtimeTenantForm.useGlobalCredentials
                                  ? 'Using global Gemini key'
                                  : (universityAiConfig?.realtime?.geminiKeyConfigured
                                    ? 'Leave blank to keep the existing key'
                                    : 'Provide your institution Gemini API key')
                              }
                            />
                            <FormControlLabel
                              control={(
                                <Switch
                                  checked={Boolean(realtimeTenantForm.proactiveAudio)}
                                  onChange={updateUniversityRealtimeField('proactiveAudio')}
                                  disabled={Boolean(realtimeTenantForm.useGlobalCredentials)}
                                />
                              )}
                              label="Enable proactive audio"
                            />
                            <FormControlLabel
                              control={(
                                <Switch
                                  checked={Boolean(realtimeTenantForm.affectiveDialog)}
                                  onChange={updateUniversityRealtimeField('affectiveDialog')}
                                  disabled={Boolean(realtimeTenantForm.useGlobalCredentials)}
                                />
                              )}
                              label="Enable affective dialog"
                            />
                          </Stack>
                        )}
                        {realtimeTenantForm.provider === 'azure' && (
                          <Stack spacing={2}>
                            <TextField
                              label="Azure endpoint"
                              value={realtimeTenantForm.azureEndpoint}
                              onChange={updateUniversityRealtimeField('azureEndpoint')}
                              helperText="Base Azure OpenAI endpoint URL (e.g., https://your-resource.openai.azure.com)"
                              disabled={Boolean(realtimeTenantForm.useGlobalCredentials)}
                            />
                            <TextField
                              label="Azure realtime endpoint (optional override)"
                              value={realtimeTenantForm.azureRealtimeEndpoint}
                              onChange={updateUniversityRealtimeField('azureRealtimeEndpoint')}
                              helperText="Full realtime URL (e.g., https://<resource>.cognitiveservices.azure.com/openai/realtime?api-version=...&deployment=...). If set, endpoint/deployment/version below are optional."
                              disabled={Boolean(realtimeTenantForm.useGlobalCredentials)}
                            />
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                              <TextField
                                label="Deployment id"
                                value={realtimeTenantForm.azureDeploymentId}
                                onChange={updateUniversityRealtimeField('azureDeploymentId')}
                                helperText="Azure deployment id for your realtime model"
                                sx={{ minWidth: 200 }}
                                disabled={Boolean(realtimeTenantForm.useGlobalCredentials)}
                              />
                              <TextField
                                label="API version"
                                value={realtimeTenantForm.azureApiVersion}
                                onChange={updateUniversityRealtimeField('azureApiVersion')}
                                helperText="Azure API version (e.g., 2024-12-01-preview)"
                                sx={{ minWidth: 200 }}
                                disabled={Boolean(realtimeTenantForm.useGlobalCredentials)}
                              />
                            </Stack>
                            <TextField
                              label="Resource name (optional)"
                              value={realtimeTenantForm.azureResourceName}
                              onChange={updateUniversityRealtimeField('azureResourceName')}
                              disabled={Boolean(realtimeTenantForm.useGlobalCredentials)}
                            />
                            <TextField
                              label="Azure OpenAI API key"
                              type="password"
                              value={realtimeTenantForm.azureApiKey}
                              onChange={updateUniversityRealtimeField('azureApiKey')}
                              disabled={Boolean(realtimeTenantForm.useGlobalCredentials)}
                              helperText={
                                realtimeTenantForm.useGlobalCredentials
                                  ? 'Using global Azure OpenAI key'
                                  : (universityAiConfig?.realtime?.azureKeyConfigured
                                    ? 'Leave blank to keep the existing key'
                                    : 'Provide the Azure OpenAI key for this tenant')
                              }
                            />
                            <FormControlLabel
                              control={(
                                <Switch
                                  checked={Boolean(realtimeTenantForm.azureUseActiveDirectory)}
                                  onChange={(event) => setUniversityAiForm((prev) => ({
                                    ...prev,
                                    realtime: {
                                      ...(prev.realtime || defaultUniversityAiForm.realtime),
                                      azureUseActiveDirectory: event.target.checked,
                                    },
                                  }))}
                                  disabled={Boolean(realtimeTenantForm.useGlobalCredentials)}
                                />
                              )}
                              label="Use Azure Active Directory (access token authentication)"
                            />
                          </Stack>
                        )}
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                          <TextField
                            label="Realtime model"
                            value={realtimeTenantForm.realtimeModel}
                            onChange={updateUniversityRealtimeField('realtimeModel')}
                            sx={{ minWidth: 240 }}
                            helperText={
                              (universityAiConfig?.allowedRealtimeModels?.length
                                ? `Enter the exact realtime model id (e.g., ${universityAiConfig.allowedRealtimeModels.slice(0, 3).join(', ')})`
                                : 'Enter the exact realtime model id configured for your tenant.')
                            }
                            autoComplete="off"
                            inputProps={{ spellCheck: 'false' }}
                          />
                          {realtimeTenantForm.provider === 'gemini' ? (
                            <TextField
                              label="Voice"
                              value={realtimeTenantForm.voice}
                              onChange={updateUniversityRealtimeField('voice')}
                              sx={{ minWidth: 240 }}
                              helperText="Gemini prebuilt voice name (for example: Kore, Puck, Charon)."
                            />
                          ) : (
                            <TextField
                              select
                              label="Voice"
                              value={realtimeTenantForm.voice}
                              onChange={updateUniversityRealtimeField('voice')}
                              sx={{ minWidth: 240 }}
                            >
                              {(universityAiConfig?.voices || settings?.openai?.voices || []).map((voice) => (
                                <MenuItem key={voice} value={voice}>{voice}</MenuItem>
                              ))}
                            </TextField>
                          )}
                        </Stack>
                      </Stack>
                    </Paper>

                    <Paper elevation={0} sx={{ p: 2 }}>
                      <Stack spacing={2}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                          <TextField
                            select
                            label="Marking provider"
                            value={markingTenantForm.provider}
                            onChange={updateUniversityMarkingField('provider')}
                            sx={{ minWidth: 200 }}
                            disabled={Boolean(markingTenantForm.useGlobalCredentials)}
                          >
                            <MenuItem value="openai">OpenAI</MenuItem>
                            <MenuItem value="azure">Azure OpenAI</MenuItem>
                          </TextField>
                          <FormControlLabel
                            control={(
                              <Switch
                                checked={Boolean(markingTenantForm.useGlobalCredentials)}
                                onChange={(event) => setUniversityAiForm((prev) => ({
                                  ...prev,
                                  marking: {
                                    ...(prev.marking || defaultUniversityAiForm.marking),
                                    useGlobalCredentials: event.target.checked,
                                  },
                                }))}
                              />
                            )}
                            label="Use global credentials"
                          />
                        </Stack>
                        {markingTenantForm.provider === 'openai' && (
                          <TextField
                            label="Parent OpenAI API key"
                            type="password"
                            value={markingTenantForm.parentApiKey}
                            onChange={updateUniversityMarkingField('parentApiKey')}
                            disabled={Boolean(markingTenantForm.useGlobalCredentials)}
                            helperText={
                              markingTenantForm.useGlobalCredentials
                                ? 'Using global OpenAI key'
                                : (universityAiConfig?.marking?.openaiKeyConfigured
                                  ? 'Leave blank to keep the existing key'
                                  : 'Provide your institution API key')
                            }
                          />
                        )}
                        {markingTenantForm.provider === 'azure' && (
                          <Stack spacing={2}>
                            <TextField
                              label="Azure endpoint"
                              value={markingTenantForm.azureEndpoint}
                              onChange={updateUniversityMarkingField('azureEndpoint')}
                              helperText="Base Azure OpenAI endpoint URL (e.g., https://your-resource.openai.azure.com) or full responses endpoint (https://your-resource.openai.azure.com/openai/responses?api-version=...)"
                              disabled={Boolean(markingTenantForm.useGlobalCredentials)}
                            />
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                              <TextField
                                label="Deployment id"
                                value={markingTenantForm.azureDeploymentId}
                                onChange={updateUniversityMarkingField('azureDeploymentId')}
                                helperText="Azure deployment id for your marking model (optional if using full responses endpoint)"
                                sx={{ minWidth: 200 }}
                                disabled={Boolean(markingTenantForm.useGlobalCredentials)}
                              />
                              <TextField
                                label="API version"
                                value={markingTenantForm.azureApiVersion}
                                onChange={updateUniversityMarkingField('azureApiVersion')}
                                helperText="Azure API version (e.g., 2025-04-01-preview). Optional if included in endpoint."
                                sx={{ minWidth: 200 }}
                                disabled={Boolean(markingTenantForm.useGlobalCredentials)}
                              />
                            </Stack>
                            <TextField
                              label="Resource name (optional)"
                              value={markingTenantForm.azureResourceName}
                              onChange={updateUniversityMarkingField('azureResourceName')}
                              disabled={Boolean(markingTenantForm.useGlobalCredentials)}
                            />
                            <TextField
                              label="Azure OpenAI API key"
                              type="password"
                              value={markingTenantForm.azureApiKey}
                              onChange={updateUniversityMarkingField('azureApiKey')}
                              disabled={Boolean(markingTenantForm.useGlobalCredentials)}
                              helperText={
                                markingTenantForm.useGlobalCredentials
                                  ? 'Using global Azure OpenAI key'
                                  : (universityAiConfig?.marking?.azureKeyConfigured
                                    ? 'Leave blank to keep the existing key'
                                    : 'Provide the Azure OpenAI key for this tenant')
                              }
                            />
                            <FormControlLabel
                              control={(
                                <Switch
                                  checked={Boolean(markingTenantForm.azureUseActiveDirectory)}
                                  onChange={(event) => setUniversityAiForm((prev) => ({
                                    ...prev,
                                    marking: {
                                      ...(prev.marking || defaultUniversityAiForm.marking),
                                      azureUseActiveDirectory: event.target.checked,
                                    },
                                  }))}
                                  disabled={Boolean(markingTenantForm.useGlobalCredentials)}
                                />
                              )}
                              label="Use Azure Active Directory (access token authentication)"
                            />
                          </Stack>
                        )}
                        <TextField
                          label="Marking model"
                          value={markingTenantForm.markingModel}
                          onChange={updateUniversityMarkingField('markingModel')}
                          sx={{ minWidth: 240 }}
                          helperText={
                            (universityAiConfig?.allowedMarkingModels?.length
                              ? `Enter the exact responses model id (e.g., ${universityAiConfig.allowedMarkingModels.slice(0, 3).join(', ')})`
                              : 'Enter the exact responses model used for marking.')
                          }
                          autoComplete="off"
                          inputProps={{ spellCheck: 'false' }}
                        />
                        <Divider />
                        <Typography variant="subtitle2">Marking parameters</Typography>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                          <TextField
                            label="Temperature"
                            type="number"
                            value={markingTenantForm.parameters?.temperature ?? ''}
                            onChange={updateMarkingParameterField('temperature')}
                            inputProps={{ min: 0, max: 2, step: 0.1 }}
                            helperText="0 for deterministic scoring"
                            sx={{ minWidth: 200 }}
                          />
                          <TextField
                            label="Max output tokens"
                            type="number"
                            value={markingTenantForm.parameters?.maxOutputTokens ?? ''}
                            onChange={updateMarkingParameterField('maxOutputTokens')}
                            helperText="Optional cap on response tokens"
                            sx={{ minWidth: 200 }}
                          />
                          <TextField
                            label="Top P"
                            type="number"
                            value={markingTenantForm.parameters?.topP ?? ''}
                            onChange={updateMarkingParameterField('topP')}
                            inputProps={{ min: 0, max: 1, step: 0.05 }}
                            helperText="Nucleus sampling"
                            sx={{ minWidth: 200 }}
                          />
                        </Stack>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                          <TextField
                            label="Frequency penalty"
                            type="number"
                            value={markingTenantForm.parameters?.frequencyPenalty ?? ''}
                            onChange={updateMarkingParameterField('frequencyPenalty')}
                            inputProps={{ min: -2, max: 2, step: 0.1 }}
                            sx={{ minWidth: 200 }}
                          />
                          <TextField
                            label="Presence penalty"
                            type="number"
                            value={markingTenantForm.parameters?.presencePenalty ?? ''}
                            onChange={updateMarkingParameterField('presencePenalty')}
                            inputProps={{ min: -2, max: 2, step: 0.1 }}
                            sx={{ minWidth: 200 }}
                          />
                          <TextField
                            label="Reasoning effort"
                            value={markingTenantForm.parameters?.reasoningEffort || ''}
                            onChange={updateMarkingParameterField('reasoningEffort')}
                            helperText="Optional: reasoning effort for GPT-5 models"
                            sx={{ minWidth: 240 }}
                          />
                        </Stack>
                        <TextField
                          select
                          label="Verbosity"
                          value={markingTenantForm.parameters?.verbosity || defaultMarkingForm.parameters.verbosity}
                          onChange={updateMarkingParameterField('verbosity')}
                          helperText="Controls feedback length (low = very concise)"
                          sx={{ minWidth: 200 }}
                        >
                          <MenuItem value="low">Low</MenuItem>
                          <MenuItem value="medium">Medium</MenuItem>
                          <MenuItem value="high">High</MenuItem>
                        </TextField>
                      </Stack>
                    </Paper>

                    <Paper elevation={0} sx={{ p: 2 }}>
                      <Stack spacing={2}>
                        <Typography variant="subtitle2">Input transcription override (optional)</Typography>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                          <FormControlLabel
                            control={(
                              <Switch
                                checked={Boolean(transcriptionTenantForm.enabled)}
                                onChange={updateUniversityTranscriptionField('enabled')}
                              />
                            )}
                            label="Enable tenant transcription override"
                          />
                          <TextField
                            select
                            label="Provider"
                            value={transcriptionTenantForm.provider}
                            onChange={updateUniversityTranscriptionField('provider')}
                            sx={{ minWidth: 200 }}
                            disabled={Boolean(transcriptionTenantForm.useGlobalCredentials)}
                          >
                            <MenuItem value="openai">OpenAI</MenuItem>
                            <MenuItem value="azure">Azure OpenAI</MenuItem>
                          </TextField>
                          <FormControlLabel
                            control={(
                              <Switch
                                checked={Boolean(transcriptionTenantForm.useGlobalCredentials)}
                                onChange={(event) => setUniversityAiForm((prev) => ({
                                  ...prev,
                                  transcription: {
                                    ...(prev.transcription || defaultUniversityAiForm.transcription),
                                    useGlobalCredentials: event.target.checked,
                                  },
                                }))}
                              />
                            )}
                            label="Use global credentials"
                          />
                        </Stack>

                        <TextField
                          label="Transcription model"
                          value={transcriptionTenantForm.model}
                          onChange={updateUniversityTranscriptionField('model')}
                          helperText="Default: gpt-4o-transcribe"
                          disabled={!transcriptionTenantForm.enabled}
                          sx={{ minWidth: 240 }}
                        />

                        {transcriptionTenantForm.provider === 'openai' && (
                          <TextField
                            label="OpenAI API key"
                            type="password"
                            value={transcriptionTenantForm.parentApiKey}
                            onChange={updateUniversityTranscriptionField('parentApiKey')}
                            disabled={!transcriptionTenantForm.enabled || Boolean(transcriptionTenantForm.useGlobalCredentials)}
                            helperText={
                              transcriptionTenantForm.useGlobalCredentials
                                ? 'Using global OpenAI transcription key'
                                : (universityAiConfig?.transcription?.openaiKeyConfigured
                                  ? 'Leave blank to keep the existing key'
                                  : 'Provide the API key for transcription requests')
                            }
                          />
                        )}

                        {transcriptionTenantForm.provider === 'azure' && (
                          <Stack spacing={2}>
                            <TextField
                              label="Azure endpoint"
                              value={transcriptionTenantForm.azureEndpoint}
                              onChange={updateUniversityTranscriptionField('azureEndpoint')}
                              helperText="Base Azure endpoint or full audio/transcriptions endpoint URL"
                              disabled={!transcriptionTenantForm.enabled || Boolean(transcriptionTenantForm.useGlobalCredentials)}
                            />
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                              <TextField
                                label="Deployment id"
                                value={transcriptionTenantForm.azureDeploymentId}
                                onChange={updateUniversityTranscriptionField('azureDeploymentId')}
                                helperText="Optional if endpoint already includes deployment."
                                sx={{ minWidth: 200 }}
                                disabled={!transcriptionTenantForm.enabled || Boolean(transcriptionTenantForm.useGlobalCredentials)}
                              />
                              <TextField
                                label="API version"
                                value={transcriptionTenantForm.azureApiVersion}
                                onChange={updateUniversityTranscriptionField('azureApiVersion')}
                                helperText="Optional if api-version is already in endpoint."
                                sx={{ minWidth: 200 }}
                                disabled={!transcriptionTenantForm.enabled || Boolean(transcriptionTenantForm.useGlobalCredentials)}
                              />
                            </Stack>
                            <TextField
                              label="Resource name (optional)"
                              value={transcriptionTenantForm.azureResourceName}
                              onChange={updateUniversityTranscriptionField('azureResourceName')}
                              disabled={!transcriptionTenantForm.enabled || Boolean(transcriptionTenantForm.useGlobalCredentials)}
                            />
                            <TextField
                              label="Azure API key"
                              type="password"
                              value={transcriptionTenantForm.azureApiKey}
                              onChange={updateUniversityTranscriptionField('azureApiKey')}
                              disabled={!transcriptionTenantForm.enabled || Boolean(transcriptionTenantForm.useGlobalCredentials)}
                              helperText={
                                transcriptionTenantForm.useGlobalCredentials
                                  ? 'Using global Azure transcription key'
                                  : (universityAiConfig?.transcription?.azureKeyConfigured
                                    ? 'Leave blank to keep the existing key'
                                    : 'Provide the Azure API key for transcription')
                              }
                            />
                            <FormControlLabel
                              control={(
                                <Switch
                                  checked={Boolean(transcriptionTenantForm.azureUseActiveDirectory)}
                                  onChange={updateUniversityTranscriptionField('azureUseActiveDirectory')}
                                  disabled={!transcriptionTenantForm.enabled || Boolean(transcriptionTenantForm.useGlobalCredentials)}
                                />
                              )}
                              label="Use Azure Active Directory"
                            />
                          </Stack>
                        )}

                        <Typography variant="body2" color="text.secondary">
                          Applies to user-input transcript text only. Realtime voice generation remains unchanged.
                        </Typography>
                      </Stack>
                    </Paper>

                    <Stack direction="row" spacing={2}>
                      <Button
                        variant="contained"
                        onClick={saveUniversityAiConfig}
                        disabled={universityAiSaving}
                      >
                        {universityAiSaving ? 'Saving...' : 'Save AI configuration'}
                      </Button>
                      <Button variant="outlined" onClick={() => loadUniversityAiConfig(selectedUniversityId)} disabled={universityAiSaving}>
                        Reset
                      </Button>
                    </Stack>
                  </Stack>
                )}
              </Stack>
            </Paper>
          </Stack>
        )}
      </Paper>
    </Stack>
  );

  const renderBillingTab = () => {
    return (
    <Stack spacing={3}>
      <Paper elevation={0} sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6">Pilot mode</Typography>
            <Typography variant="body2" color="text.secondary">
              Toggle between pilot and live behaviour for individual users.
            </Typography>
          </Box>
          <FormControlLabel
            control={(
              <Switch
                checked={Boolean(pilotForm.enabled)}
                onChange={handlePilotFieldChange('enabled')}
              />
            )}
            label={pilotForm.enabled ? 'Pilot mode active' : 'Live mode active'}
          />
          <Typography variant="body2" color="text.secondary">
            Pilot mode hides Google/Microsoft OAuth buttons on login/signup and hides individual billing UI.
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={savePilotSettings}
              disabled={savingPilot}
            >
              {savingPilot ? 'Saving...' : 'Save pilot mode'}
            </Button>
            <Button variant="outlined" onClick={loadSettings} disabled={savingPilot}>Reset</Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Wallet defaults</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Complimentary tokens"
              type="number"
              fullWidth
              value={billingForm.freeSignupTokens}
              onChange={handleBillingFieldChange('freeSignupTokens')}
              helperText="Granted to individuals at sign-up"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Tokens per history"
              type="number"
              fullWidth
              value={billingForm.historyCostTokens}
              onChange={handleBillingFieldChange('historyCostTokens')}
              helperText="Consumed when a history is submitted"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="User registration cap"
              type="number"
              fullWidth
              value={billingForm.userRegistrationCap}
              onChange={handleBillingFieldChange('userRegistrationCap')}
              helperText="Set to 0 for unlimited"
            />
          </Grid>
        </Grid>
        <Stack direction="row" spacing={4} sx={{ mt: 2 }}>
          <FormControlLabel
            control={(
              <Switch
                checked={Boolean(billingForm.receiptsEnabled)}
                onChange={handleBillingFieldChange('receiptsEnabled')}
              />
            )}
            label="Stripe receipts enabled"
          />
          <FormControlLabel
            control={(
              <Switch
                checked={Boolean(billingForm.vatInvoicesEnabled)}
                onChange={handleBillingFieldChange('vatInvoicesEnabled')}
              />
            )}
            label="VAT invoices enabled"
          />
        </Stack>
        <Stack spacing={2} sx={{ mt: 2 }}>
          <TextField
            label="Refund policy"
            multiline
            minRows={3}
            value={billingForm.refundPolicy}
            onChange={handleBillingFieldChange('refundPolicy')}
          />
          <TextField
            label="Refund window (days)"
            type="number"
            value={billingForm.refundWindowDays}
            onChange={handleBillingFieldChange('refundWindowDays')}
            sx={{ maxWidth: 240 }}
          />
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">Token packs</Typography>
          <Button variant="outlined" onClick={addTokenPack}>Add pack</Button>
        </Stack>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Tokens</TableCell>
              <TableCell>Price (GBP)</TableCell>
              <TableCell>Histories</TableCell>
              <TableCell>Stripe price ID</TableCell>
              <TableCell>Active</TableCell>
              <TableCell width={80}>
                <span className="sr-only">Actions</span>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {billingForm.tokenPacks.map((pack, index) => (
              <TableRow key={pack.key || index}>
                <TableCell>
                  <TextField
                    label="Pack name"
                    value={pack.name}
                    onChange={handlePackFieldChange(index, 'name')}
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    label="Tokens"
                    type="number"
                    value={pack.tokens}
                    onChange={handlePackFieldChange(index, 'tokens')}
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    label="Price (GBP)"
                    type="number"
                    value={pack.price}
                    onChange={handlePackFieldChange(index, 'price')}
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    label="Histories"
                    type="number"
                    value={pack.historiesEquivalent}
                    onChange={handlePackFieldChange(index, 'historiesEquivalent')}
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    label="Stripe price ID"
                    value={pack.stripePriceId}
                    onChange={handlePackFieldChange(index, 'stripePriceId')}
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={Boolean(pack.isActive)}
                        onChange={handlePackFieldChange(index, 'isActive')}
                      />
                    )}
                    label="Enabled"
                  />
                </TableCell>
                <TableCell>
                  <Button color="error" onClick={() => removeTokenPack(index)}>
                    Remove
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!billingForm.tokenPacks.length && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No token packs defined. Add a pack to begin selling tokens.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={saveBillingSettings}
          disabled={savingBilling}
        >
          {savingBilling ? 'Saving...' : 'Save billing settings'}
        </Button>
        <Button variant="outlined" onClick={loadSettings}>Reset</Button>
      </Stack>

      <Paper elevation={0} sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6">Unity addressables</Typography>
            <Typography variant="body2" color="text.secondary">
              Upload remote addressable builds for each Unity client platform.
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {UNITY_ADDRESSABLE_PLATFORMS.map((platform) => {
              const current = addressablesByPlatform[platform.key];
              const formState = addressablesForm[platform.key] || {};
              const uploadState = addressablesUploadState[platform.key] || {};
              const fileCount = Number.isFinite(Number(current?.fileCount)) ? Number(current.fileCount) : 0;
              const totalBytes = Number.isFinite(Number(current?.totalBytes)) ? Number(current.totalBytes) : 0;

              return (
                <Grid item xs={12} md={6} key={platform.key}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Stack spacing={1.5}>
                      <Typography variant="subtitle1">{platform.label}</Typography>
                      <TextField
                        label="Base URL"
                        value={current?.baseUrl || ''}
                        placeholder="Not uploaded yet"
                        InputProps={{ readOnly: true }}
                        fullWidth
                      />
                      <Stack spacing={0.5}>
                        <Typography variant="caption" color="text.secondary">
                          {current?.version ? `Current version: ${current.version}` : 'Current version: not set'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {current?.uploadedAt
                            ? `Last upload: ${formatRelativeTime(current.uploadedAt)}`
                            : 'Last upload: not yet'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {`${formatInteger(fileCount)} files \u2022 ${formatBytes(totalBytes)}`}
                        </Typography>
                      </Stack>

                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                        <Button variant="outlined" component="label">
                          {formState.file ? 'Replace .zip' : 'Choose .zip'}
                          <input
                            hidden
                            type="file"
                            accept=".zip"
                            onChange={handleAddressablesFieldChange(platform.key, 'file')}
                          />
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                          {formState.file?.name || 'No file selected'}
                        </Typography>
                      </Stack>

                      <TextField
                        label="Version (optional)"
                        value={formState.version || ''}
                        onChange={handleAddressablesFieldChange(platform.key, 'version')}
                        fullWidth
                      />

                      {uploadState.error && (
                        <Alert severity="error">
                          {uploadState.error}
                        </Alert>
                      )}

                      <Button
                        variant="contained"
                        onClick={() => handleUploadAddressables(platform.key)}
                        disabled={!formState.file || uploadState.uploading}
                      >
                        {uploadState.uploading ? 'Uploading...' : 'Upload build'}
                      </Button>
                    </Stack>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2}>
            <Typography variant="h6">Credit invoices</Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
              <TextField
                select
                label="Status"
                value={creditInvoiceFilters.status}
                onChange={handleCreditInvoiceFilterChange('status')}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="all">All statuses</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
              </TextField>
              <Button variant="outlined" onClick={loadCreditInvoices} disabled={creditInvoicesLoading}>
                {creditInvoicesLoading ? 'Refreshing...' : 'Refresh'}
              </Button>
            </Stack>
          </Stack>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                select
                label="University"
                value={creditInvoiceCreateForm.universityId}
                onChange={(event) => setCreditInvoiceCreateForm((prev) => ({ ...prev, universityId: event.target.value }))}
                fullWidth
              >
                <MenuItem value="">
                  <em>Select a university</em>
                </MenuItem>
                {(tenantSnapshot?.tenants || []).map((tenant) => (
                  <MenuItem key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Credits"
                type="number"
                value={creditInvoiceCreateForm.quantity}
                onChange={(event) => setCreditInvoiceCreateForm((prev) => ({ ...prev, quantity: event.target.value }))}
                inputProps={{ min: 1 }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="contained"
                onClick={handleCreateCreditInvoice}
                disabled={creatingCreditInvoice}
                fullWidth
              >
                {creatingCreditInvoice ? 'Creating...' : 'Create draft invoice'}
              </Button>
            </Grid>
          </Grid>

          <Divider />

          {creditInvoiceAlert && (
            <Alert severity={creditInvoiceAlert.severity} onClose={() => setCreditInvoiceAlert(null)}>
              {creditInvoiceAlert.message}
            </Alert>
          )}

          {creditInvoicesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : creditInvoices.length === 0 ? (
            <Alert severity="info">No credit invoices found.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>University</TableCell>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Credits</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Requested</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {creditInvoices.map((invoice) => {
                  const isSending = creditInvoiceActions.sending === invoice.id;
                  const isMarkingPaid = creditInvoiceActions.markingPaid === invoice.id;
                  const isDownloading = creditInvoiceActions.downloading === invoice.id;
                  const canSend = invoice.status === 'draft';
                  const canMarkPaid = invoice.status !== 'paid';
                  return (
                    <TableRow key={invoice.id}>
                      <TableCell>{invoice.universityName || invoice.universityId}</TableCell>
                      <TableCell>{invoice.invoiceNumber}</TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{invoice.status}</TableCell>
                      <TableCell>{formatInteger(invoice.quantity)}</TableCell>
                      <TableCell>{formatCurrency(invoice.totalCents, invoice.currency)}</TableCell>
                      <TableCell>{formatRelativeTime(invoice.requestedAt || invoice.createdAt)}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          {invoice.stripeHostedInvoiceUrl && (
                            <Button
                              size="small"
                              component="a"
                              href={invoice.stripeHostedInvoiceUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Stripe
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleDownloadCreditInvoicePdf(invoice)}
                            disabled={isDownloading}
                          >
                            {isDownloading ? 'Downloading...' : 'PDF'}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleSendCreditInvoice(invoice.id)}
                            disabled={!canSend || isSending}
                          >
                            {isSending ? 'Sending...' : 'Send invoice'}
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleMarkCreditInvoicePaid(invoice.id)}
                            disabled={!canMarkPaid || isMarkingPaid}
                          >
                            {isMarkingPaid ? 'Marking...' : 'Mark paid'}
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
  };

  const renderRealtimeTab = () => (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Typography variant="h6">Realtime defaults for individual learners</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              select
              label="Provider"
              value={realtimeForm.provider}
              onChange={(event) => setRealtimeForm((prev) => ({ ...prev, provider: event.target.value }))}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="openai">OpenAI</MenuItem>
              <MenuItem value="azure">Azure OpenAI</MenuItem>
              <MenuItem value="gemini">Gemini Live</MenuItem>
            </TextField>
          </Stack>
        {realtimeForm.provider === 'openai' && (
          <TextField
            label="OpenAI API key"
            type="password"
            value={realtimeForm.parentApiKey}
            onChange={(event) => setRealtimeForm((prev) => ({ ...prev, parentApiKey: event.target.value }))}
            helperText={settings?.openai?.realtime?.openaiKeyConfigured ? 'Leave blank to keep the existing key' : 'Provide the API key used to mint realtime client secrets'}
          />
        )}
        {realtimeForm.provider === 'gemini' && (
          <Stack spacing={2}>
            <TextField
              label="Gemini API key"
              type="password"
              value={realtimeForm.parentApiKey}
              onChange={(event) => setRealtimeForm((prev) => ({ ...prev, parentApiKey: event.target.value }))}
              helperText={settings?.openai?.realtime?.geminiKeyConfigured ? 'Leave blank to keep the existing key' : 'Provide the Gemini API key used to mint ephemeral auth tokens'}
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={Boolean(realtimeForm.proactiveAudio)}
                  onChange={(event) => setRealtimeForm((prev) => ({ ...prev, proactiveAudio: event.target.checked }))}
                />
              )}
              label="Enable proactive audio"
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={Boolean(realtimeForm.affectiveDialog)}
                  onChange={(event) => setRealtimeForm((prev) => ({ ...prev, affectiveDialog: event.target.checked }))}
                />
              )}
              label="Enable affective dialog"
            />
          </Stack>
        )}
        {realtimeForm.provider === 'azure' && (
          <Stack spacing={2}>
            <TextField
              label="Azure endpoint"
              value={realtimeForm.azureEndpoint}
              onChange={(event) => setRealtimeForm((prev) => ({ ...prev, azureEndpoint: event.target.value }))}
              helperText="Base Azure OpenAI endpoint URL (e.g., https://your-resource.openai.azure.com)"
            />
            <TextField
              label="Azure realtime endpoint (optional override)"
              value={realtimeForm.azureRealtimeEndpoint}
              onChange={(event) => setRealtimeForm((prev) => ({ ...prev, azureRealtimeEndpoint: event.target.value }))}
              helperText="Full realtime URL (e.g., https://<resource>.cognitiveservices.azure.com/openai/realtime?api-version=...&deployment=...). If set, endpoint/deployment/version below are optional."
            />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Deployment id"
                value={realtimeForm.azureDeploymentId}
                onChange={(event) => setRealtimeForm((prev) => ({ ...prev, azureDeploymentId: event.target.value }))}
                helperText="Azure deployment id for your realtime model"
                sx={{ minWidth: 200 }}
              />
              <TextField
                label="API version"
                value={realtimeForm.azureApiVersion}
                onChange={(event) => setRealtimeForm((prev) => ({ ...prev, azureApiVersion: event.target.value }))}
                helperText="Azure API version (e.g., 2024-12-01-preview)"
                sx={{ minWidth: 200 }}
              />
            </Stack>
            <TextField
              label="Resource name (optional)"
              value={realtimeForm.azureResourceName}
              onChange={(event) => setRealtimeForm((prev) => ({ ...prev, azureResourceName: event.target.value }))}
              helperText="Resource name used for logging or diagnostics"
            />
            <TextField
              label="Azure OpenAI API key"
              type="password"
              value={realtimeForm.azureApiKey}
              onChange={(event) => setRealtimeForm((prev) => ({ ...prev, azureApiKey: event.target.value }))}
              helperText={settings?.openai?.realtime?.azureKeyConfigured ? 'Leave blank to keep the existing key' : 'Provide the Azure OpenAI key for this deployment'}
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={Boolean(realtimeForm.azureUseActiveDirectory)}
                  onChange={(event) => setRealtimeForm((prev) => ({ ...prev, azureUseActiveDirectory: event.target.checked }))}
                />
              )}
              label="Use Azure Active Directory (access token authentication)"
            />
          </Stack>
        )}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Realtime model"
            value={realtimeForm.realtimeModel}
            onChange={(event) => setRealtimeForm((prev) => ({ ...prev, realtimeModel: event.target.value }))}
            sx={{ minWidth: 240 }}
            helperText={
              realtimeForm.provider === 'gemini'
                ? 'Enter the exact Gemini Live model id (for example: gemini-2.5-flash-native-audio-latest).'
                : ((settings?.openai?.allowedRealtimeModels?.length
                  ? `Enter the exact OpenAI realtime model id (e.g., ${settings.openai.allowedRealtimeModels.slice(0, 3).join(', ')})`
                  : 'Enter the exact OpenAI realtime model id to use.'))
            }
            autoComplete="off"
            inputProps={{ spellCheck: 'false' }}
          />
          {realtimeForm.provider === 'gemini' ? (
            <TextField
              label="Voice preset"
              value={realtimeForm.voice}
              onChange={(event) => setRealtimeForm((prev) => ({ ...prev, voice: event.target.value }))}
              sx={{ minWidth: 240 }}
              helperText="Gemini prebuilt voice name (for example: Kore, Puck, Charon)."
            />
          ) : (
            <TextField
              select
              label="Voice preset"
              value={realtimeForm.voice}
              onChange={(event) => setRealtimeForm((prev) => ({ ...prev, voice: event.target.value }))}
              sx={{ minWidth: 240 }}
            >
              {(settings?.openai?.voices || []).map((voice) => (
                <MenuItem key={voice} value={voice}>{voice}</MenuItem>
              ))}
            </TextField>
          )}
        </Stack>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Typography variant="subtitle1">Case editor voice samples</Typography>
            <Typography variant="body2" color="text.secondary">
              Upload static sample clips used by the case editor Play sample buttons.
              {' Files are stored under `uploads/voice-samples/<provider>/<voice>.mp3|wav`.'}
            </Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                select
                label="Provider"
                value={voiceSampleForm.provider}
                onChange={handleVoiceSampleProviderChange}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="openai">OpenAI</MenuItem>
                <MenuItem value="gemini">Gemini</MenuItem>
              </TextField>
              <TextField
                select
                label="Voice"
                value={voiceSampleForm.voice}
                onChange={handleVoiceSampleVoiceChange}
                sx={{ minWidth: 260 }}
              >
                {voiceOptionsForProvider.length === 0 ? (
                  <MenuItem value="">
                    <em>No voices available</em>
                  </MenuItem>
                ) : (
                  voiceOptionsForProvider.map((voice) => (
                    <MenuItem key={voice} value={voice}>{voice}</MenuItem>
                  ))
                )}
              </TextField>
            </Stack>

            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">
                Samples uploaded: {formatInteger(voiceSamplesOverview?.counts?.total || 0)} total
                ({formatInteger(voiceSamplesOverview?.counts?.openai || 0)} OpenAI, {formatInteger(voiceSamplesOverview?.counts?.gemini || 0)} Gemini)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedVoiceSample?.hasSample
                  ? `Current file: ${selectedVoiceSample.fileName} • ${formatBytes(selectedVoiceSample.sizeBytes)} • updated ${formatRelativeTime(selectedVoiceSample.updatedAt, 'just now')}`
                  : 'Current file: not uploaded for this voice'}
              </Typography>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <Button variant="outlined" component="label">
                {voiceSampleForm.file ? 'Replace audio' : 'Choose audio'}
                <input
                  hidden
                  type="file"
                  accept=".mp3,.wav,audio/mpeg,audio/wav"
                  onChange={handleVoiceSampleFileChange}
                />
              </Button>
              <Typography variant="body2" color="text.secondary">
                {voiceSampleForm.file?.name || 'No file selected'}
              </Typography>
              {selectedVoiceSample?.hasSample && selectedVoiceSample?.mediaUrl && (
                <Button
                  variant="text"
                  size="small"
                  component="a"
                  href={selectedVoiceSample.mediaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open current sample
                </Button>
              )}
            </Stack>

            {voiceSampleActionState.error && (
              <Alert severity="error">
                {voiceSampleActionState.error}
              </Alert>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                variant="contained"
                onClick={handleUploadVoiceSample}
                disabled={!voiceSampleForm.file || voiceSampleActionState.uploading}
              >
                {voiceSampleActionState.uploading ? 'Uploading...' : 'Upload sample'}
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={handleDeleteVoiceSample}
                disabled={!selectedVoiceSample?.hasSample || voiceSampleActionState.deleting}
              >
                {voiceSampleActionState.deleting ? 'Removing...' : 'Remove sample'}
              </Button>
              <Button variant="outlined" onClick={loadSettings} disabled={loadingSettings}>
                {loadingSettings ? 'Refreshing...' : 'Refresh'}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Divider />
        <Stack spacing={2}>
          <Typography variant="subtitle1">Input transcription override (optional)</Typography>
          <FormControlLabel
            control={(
              <Switch
                checked={Boolean(transcriptionForm.enabled)}
                onChange={(event) => setTranscriptionForm((prev) => ({ ...prev, enabled: event.target.checked }))}
              />
            )}
            label="Override user input transcription with backend gpt-4o-transcribe pipeline"
          />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              select
              label="Transcription provider"
              value={transcriptionForm.provider}
              onChange={(event) => setTranscriptionForm((prev) => ({ ...prev, provider: event.target.value }))}
              sx={{ minWidth: 200 }}
              disabled={!transcriptionForm.enabled}
            >
              <MenuItem value="openai">OpenAI</MenuItem>
              <MenuItem value="azure">Azure OpenAI</MenuItem>
            </TextField>
            <TextField
              label="Transcription model"
              value={transcriptionForm.model}
              onChange={(event) => setTranscriptionForm((prev) => ({ ...prev, model: event.target.value }))}
              sx={{ minWidth: 240 }}
              helperText="Recommended: gpt-4o-transcribe"
              disabled={!transcriptionForm.enabled}
            />
          </Stack>
          {transcriptionForm.provider === 'openai' && (
            <TextField
              label="OpenAI API key"
              type="password"
              value={transcriptionForm.parentApiKey}
              onChange={(event) => setTranscriptionForm((prev) => ({ ...prev, parentApiKey: event.target.value }))}
              helperText={settings?.openai?.transcription?.openaiKeyConfigured ? 'Leave blank to keep the existing key' : 'Provide the API key used for transcription calls'}
              disabled={!transcriptionForm.enabled}
            />
          )}
          {transcriptionForm.provider === 'azure' && (
            <Stack spacing={2}>
              <TextField
                label="Azure endpoint"
                value={transcriptionForm.azureEndpoint}
                onChange={(event) => setTranscriptionForm((prev) => ({ ...prev, azureEndpoint: event.target.value }))}
                helperText="Base endpoint or full audio/transcriptions endpoint URL"
                disabled={!transcriptionForm.enabled}
              />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Deployment id"
                  value={transcriptionForm.azureDeploymentId}
                  onChange={(event) => setTranscriptionForm((prev) => ({ ...prev, azureDeploymentId: event.target.value }))}
                  helperText="Optional if full endpoint already contains deployment"
                  sx={{ minWidth: 200 }}
                  disabled={!transcriptionForm.enabled}
                />
                <TextField
                  label="API version"
                  value={transcriptionForm.azureApiVersion}
                  onChange={(event) => setTranscriptionForm((prev) => ({ ...prev, azureApiVersion: event.target.value }))}
                  helperText="Required unless already included in endpoint"
                  sx={{ minWidth: 200 }}
                  disabled={!transcriptionForm.enabled}
                />
              </Stack>
              <TextField
                label="Resource name (optional)"
                value={transcriptionForm.azureResourceName}
                onChange={(event) => setTranscriptionForm((prev) => ({ ...prev, azureResourceName: event.target.value }))}
                disabled={!transcriptionForm.enabled}
              />
              <TextField
                label="Azure OpenAI API key"
                type="password"
                value={transcriptionForm.azureApiKey}
                onChange={(event) => setTranscriptionForm((prev) => ({ ...prev, azureApiKey: event.target.value }))}
                helperText={settings?.openai?.transcription?.azureKeyConfigured ? 'Leave blank to keep the existing key' : 'Provide the Azure API key for transcription'}
                disabled={!transcriptionForm.enabled}
              />
              <FormControlLabel
                control={(
                  <Switch
                    checked={Boolean(transcriptionForm.azureUseActiveDirectory)}
                    onChange={(event) => setTranscriptionForm((prev) => ({ ...prev, azureUseActiveDirectory: event.target.checked }))}
                    disabled={!transcriptionForm.enabled}
                  />
                )}
                label="Use Azure Active Directory (access token authentication)"
              />
            </Stack>
          )}
          <Typography variant="body2" color="text.secondary">
            This only affects user-input transcript text used in chat display and marking payloads. Realtime audio generation remains unchanged.
          </Typography>
        </Stack>

        {settings?.openai?.realtime?.lastUpdatedAt && (
          <Typography variant="body2" color="text.secondary">
            Last updated {formatDistanceToNowStrict(new Date(settings.openai.realtime.lastUpdatedAt))} ago
          </Typography>
        )}

        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => saveOpenAiSettings('realtime')}
            disabled={savingRealtime}
          >
            {savingRealtime ? 'Saving...' : 'Save realtime defaults'}
          </Button>
          <Button variant="outlined" onClick={loadSettings}>Reset</Button>
        </Stack>
      </Stack>
    </Paper>
  );

  const renderMarkingTab = () => (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Typography variant="h6">Marking defaults for backend scoring</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            select
            label="Provider"
            value={markingForm.provider}
            onChange={(event) => setMarkingForm((prev) => ({ ...prev, provider: event.target.value }))}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="openai">OpenAI</MenuItem>
            <MenuItem value="azure">Azure OpenAI</MenuItem>
          </TextField>
        </Stack>
        {markingForm.provider === 'openai' && (
          <TextField
            label="OpenAI API key"
            type="password"
            value={markingForm.parentApiKey}
            onChange={(event) => setMarkingForm((prev) => ({ ...prev, parentApiKey: event.target.value }))}
            helperText={settings?.openai?.marking?.openaiKeyConfigured ? 'Leave blank to keep the existing key' : 'Provide the API key used to call the Responses API'}
          />
        )}
        {markingForm.provider === 'azure' && (
          <Stack spacing={2}>
            <TextField
              label="Azure endpoint"
              value={markingForm.azureEndpoint}
              onChange={(event) => setMarkingForm((prev) => ({ ...prev, azureEndpoint: event.target.value }))}
              helperText="Base Azure OpenAI endpoint URL (e.g., https://your-resource.openai.azure.com) or full responses endpoint (https://your-resource.openai.azure.com/openai/responses?api-version=...)"
            />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Deployment id"
                value={markingForm.azureDeploymentId}
                onChange={(event) => setMarkingForm((prev) => ({ ...prev, azureDeploymentId: event.target.value }))}
                helperText="Azure deployment id for your marking model (optional if using full responses endpoint)"
                sx={{ minWidth: 200 }}
              />
              <TextField
                label="API version"
                value={markingForm.azureApiVersion}
                onChange={(event) => setMarkingForm((prev) => ({ ...prev, azureApiVersion: event.target.value }))}
                helperText="Azure API version (e.g., 2025-04-01-preview). Optional if included in endpoint."
                sx={{ minWidth: 200 }}
              />
            </Stack>
            <TextField
              label="Resource name (optional)"
              value={markingForm.azureResourceName}
              onChange={(event) => setMarkingForm((prev) => ({ ...prev, azureResourceName: event.target.value }))}
              helperText="Resource name used for logging or diagnostics"
            />
            <TextField
              label="Azure OpenAI API key"
              type="password"
              value={markingForm.azureApiKey}
              onChange={(event) => setMarkingForm((prev) => ({ ...prev, azureApiKey: event.target.value }))}
              helperText={settings?.openai?.marking?.azureKeyConfigured ? 'Leave blank to keep the existing key' : 'Provide the Azure OpenAI key for this deployment'}
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={Boolean(markingForm.azureUseActiveDirectory)}
                  onChange={(event) => setMarkingForm((prev) => ({ ...prev, azureUseActiveDirectory: event.target.checked }))}
                />
              )}
              label="Use Azure Active Directory (access token authentication)"
            />
          </Stack>
        )}
        <TextField
          label="Marking model"
          value={markingForm.markingModel}
          onChange={(event) => setMarkingForm((prev) => ({ ...prev, markingModel: event.target.value }))}
          sx={{ minWidth: 240 }}
          helperText={
            (settings?.openai?.allowedMarkingModels?.length
              ? `Enter the exact responses model id (e.g., ${settings.openai.allowedMarkingModels.slice(0, 3).join(', ')})`
              : 'Enter the exact Responses/GPT model to use for scoring.')
          }
          autoComplete="off"
          inputProps={{ spellCheck: 'false' }}
        />
        <Typography variant="subtitle1">Marking parameters (GPT-5 ready)</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Temperature"
              type="number"
              value={markingForm.parameters.temperature}
              onChange={(event) => setMarkingForm((prev) => ({ ...prev, parameters: { ...prev.parameters, temperature: event.target.value } }))}
              helperText="Default 0 for deterministic scoring"
              fullWidth
              inputProps={{ step: 0.1, min: 0, max: 2 }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Max output tokens"
              type="number"
              value={markingForm.parameters.maxOutputTokens}
              onChange={(event) => setMarkingForm((prev) => ({ ...prev, parameters: { ...prev.parameters, maxOutputTokens: event.target.value } }))}
              helperText="Leave blank to let the model decide"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Top P"
              type="number"
              value={markingForm.parameters.topP}
              onChange={(event) => setMarkingForm((prev) => ({ ...prev, parameters: { ...prev.parameters, topP: event.target.value } }))}
              helperText="Optional nucleus sampling (0-1)"
              fullWidth
              inputProps={{ step: 0.05, min: 0, max: 1 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Frequency penalty"
              type="number"
              value={markingForm.parameters.frequencyPenalty}
              onChange={(event) => setMarkingForm((prev) => ({ ...prev, parameters: { ...prev.parameters, frequencyPenalty: event.target.value } }))}
              helperText="Discourage repetition (-2 to 2)"
              fullWidth
              inputProps={{ step: 0.1, min: -2, max: 2 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Presence penalty"
              type="number"
              value={markingForm.parameters.presencePenalty}
              onChange={(event) => setMarkingForm((prev) => ({ ...prev, parameters: { ...prev.parameters, presencePenalty: event.target.value } }))}
              helperText="Encourage diverse points (-2 to 2)"
              fullWidth
              inputProps={{ step: 0.1, min: -2, max: 2 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Reasoning effort"
              value={markingForm.parameters.reasoningEffort}
              onChange={(event) => setMarkingForm((prev) => ({ ...prev, parameters: { ...prev.parameters, reasoningEffort: event.target.value } }))}
              helperText="Optional reasoning mode hint (e.g., medium, high)"
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              label="Verbosity"
              value={markingForm.parameters.verbosity}
              onChange={(event) => setMarkingForm((prev) => ({ ...prev, parameters: { ...prev.parameters, verbosity: event.target.value } }))}
              helperText="Controls comment length (low = concise)"
              fullWidth
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </TextField>
          </Grid>
        </Grid>

        {settings?.openai?.marking?.lastUpdatedAt && (
          <Typography variant="body2" color="text.secondary">
            Last updated {formatDistanceToNowStrict(new Date(settings.openai.marking.lastUpdatedAt))} ago
          </Typography>
        )}

        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => saveOpenAiSettings('marking')}
            disabled={savingMarking}
          >
            {savingMarking ? 'Saving...' : 'Save marking defaults'}
          </Button>
          <Button variant="outlined" onClick={loadSettings}>Reset</Button>
        </Stack>
      </Stack>
    </Paper>
  );

  const renderComplianceTab = () => {
    const receiptsEnabled = financeOverview?.billing?.receiptsEnabled ?? false;
    const vatInvoicesEnabled = financeOverview?.billing?.vatInvoicesEnabled ?? false;
    const refundWindow = financeOverview?.billing?.refundWindowDays ?? 0;
    const refundPolicy = financeOverview?.billing?.refundPolicy || 'No refund policy recorded';
    const userCap = financeOverview?.userCap || {};
    const userCapLimit = userCap.limit;
    const userCapLabel = userCapLimit
      ? `${userCap.current || 0} / ${userCapLimit}`
      : `${userCap.current || 0} users`;
    const userCapDescription = userCapLimit
      ? `${userCap.remaining ?? 0} seats remaining`
      : 'Unlimited registrations';

    return (
      <Stack spacing={3}>
        <Paper elevation={0} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Compliance</Typography>
          {settings?.compliance?.termsUrl ? (
            <Stack spacing={1}>
              <Typography variant="body1">Terms &amp; Conditions URL</Typography>
              <Typography variant="body2" color="text.secondary">{settings.compliance.termsUrl}</Typography>
              <Typography variant="body2" color="text.secondary">
                Current version: {settings.compliance.termsVersion || 'Unversioned'}
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Terms &amp; Conditions have not been configured yet. Publish your legal copy and set the URL here so that onboarding flows can reference it.
            </Typography>
          )}
        </Paper>

        <Paper elevation={0} sx={{ p: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2}>
            <Typography variant="h6">Finance &amp; billing oversight</Typography>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportFinanceReport}
              disabled={exportingFinance || loadingFinance}
            >
              {exportingFinance ? 'Exporting...' : 'Export CSV'}
            </Button>
          </Stack>

          {loadingFinance ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : !financeOverview ? (
            <Typography variant="body2" color="text.secondary">Unable to load finance overview.</Typography>
          ) : (
            <Stack spacing={3} sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <MetricCard
                    title="Receipts enabled"
                    value={receiptsEnabled ? 'Yes' : 'No'}
                    description={`VAT invoices ${vatInvoicesEnabled ? 'enabled' : 'disabled'}`}
                    icon={<CheckCircleIcon color={receiptsEnabled ? 'success' : 'warning'} />}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <MetricCard
                    title="Refund window"
                    value={`${refundWindow} days`}
                    description={refundPolicy}
                    icon={<WarningAmberIcon color="warning" />}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <MetricCard
                    title="User cap"
                    value={userCapLabel}
                    description={userCapDescription}
                    icon={<CheckCircleIcon color="primary" />}
                  />
                </Grid>
              </Grid>

              <Stack spacing={2}>
                <Typography variant="subtitle1">Recent refunds</Typography>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Entity</TableCell>
                        <TableCell>Credits</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Occurred</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(financeOverview.refunds || []).slice(0, 5).map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell>{entry.entityName}</TableCell>
                          <TableCell>{entry.credits}</TableCell>
                          <TableCell>{formatCurrency(entry.amountCents, entry.currency)}</TableCell>
                          <TableCell>{formatRelativeTime(entry.occurredAt)}</TableCell>
                        </TableRow>
                      ))}
                      {!(financeOverview.refunds || []).length && (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography variant="body2" color="text.secondary">No refunds recorded.</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </Stack>

              <Stack spacing={2}>
                <Typography variant="subtitle1">Recent receipts</Typography>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Entity</TableCell>
                        <TableCell>Credits</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Occurred</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(financeOverview.receipts || []).slice(0, 5).map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell>{entry.entityName}</TableCell>
                          <TableCell>{entry.credits}</TableCell>
                          <TableCell>{formatCurrency(entry.amountCents, entry.currency)}</TableCell>
                          <TableCell>{formatRelativeTime(entry.occurredAt)}</TableCell>
                        </TableRow>
                      ))}
                      {!(financeOverview.receipts || []).length && (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography variant="body2" color="text.secondary">No receipts recorded.</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </Stack>

              <Stack spacing={2}>
                <Typography variant="subtitle1">VAT invoices</Typography>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>University</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Recorded</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(financeOverview.invoices || []).slice(0, 5).map((invoice) => (
                        <TableRow key={invoice.invoiceId}>
                          <TableCell>{invoice.universityName}</TableCell>
                          <TableCell>{invoice.status}</TableCell>
                          <TableCell>{formatCurrency(invoice.amountPaid || invoice.amountDue, invoice.currency)}</TableCell>
                          <TableCell>{formatRelativeTime(invoice.recordedAt || invoice.paidAt || invoice.dueDate)}</TableCell>
                        </TableRow>
                      ))}
                      {!(financeOverview.invoices || []).length && (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography variant="body2" color="text.secondary">No invoices recorded.</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </Stack>
            </Stack>
          )}
        </Paper>
      </Stack>
    );
  };

  const renderFeaturesTab = () => (
    <Stack spacing={2}>
      {(featureChecklist || []).map((item) => (
        <Paper key={item.id} elevation={0} sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Box>
              <Typography variant="subtitle1">{item.title}</Typography>
              <Typography variant="body2" color="text.secondary">{item.description}</Typography>
            </Box>
            <Chip
              label={item.status?.replace('-', ' ') || 'planned'}
              color={item.status === 'complete' ? 'success' : item.status === 'in-progress' ? 'warning' : 'default'}
            />
          </Stack>
        </Paper>
      ))}
      {!featureChecklist.length && (
        <Typography variant="body2" color="text.secondary">
          No feature recommendations recorded yet.
        </Typography>
      )}
    </Stack>
  );

  const renderActiveTab = () => {
    switch (TAB_KEYS[activeTab]) {
      case 'overview':
        return renderOverviewTab();
      case 'universities':
        return renderUniversitiesTab();
      case 'case-transfer':
        return <CaseTransferManager />;
      case 'billing':
        return renderBillingTab();
      case 'realtime':
        return renderRealtimeTab();
      case 'marking':
        return renderMarkingTab();
      case 'compliance':
        return renderComplianceTab();
      case 'features':
        return renderFeaturesTab();
      default:
        return null;
    }
  };

    return (
      <Box sx={{ p: 3 }} onKeyDown={handleTabKeyDown}>
        <Typography variant="h4" sx={{ mb: 2 }}>Global admin console</Typography>
        {(error || successMessage) && (
          <Box sx={{ mb: 2 }}>
            {error && (
              <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
            )}
            {successMessage && (
              <Alert severity="success" onClose={() => setSuccessMessage('')}>{successMessage}</Alert>
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <IconButton
            aria-label="Previous tab"
            onClick={() => setActiveTab((prev) => Math.max(prev - 1, 0))}
            disabled={activeTab === 0}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Tabs
            value={activeTab}
            onChange={(_, index) => setActiveTab(index)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ flex: 1 }}
          >
            <Tab label="Overview" />
            <Tab label="Universities" />
            <Tab label="Case transfer" />
            <Tab label="Billing" />
            <Tab label="Realtime API" />
            <Tab label="Marking" />
            <Tab label="Compliance" />
            <Tab label="Feature checklist" />
          </Tabs>
          <IconButton
            aria-label="Next tab"
            onClick={() => setActiveTab((prev) => Math.min(prev + 1, TAB_KEYS.length - 1))}
            disabled={activeTab === TAB_KEYS.length - 1}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {renderActiveTab()}

        <Dialog open={createDialogOpen} onClose={closeCreateUniversityDialog} fullWidth maxWidth="sm">
          <DialogTitle>Create a new university</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Name"
                value={createForm.name}
                onChange={handleCreateFieldChange('name')}
                error={Boolean(createFormErrors.name)}
                helperText={createFormErrors.name || 'Enter the institution display name'}
                autoFocus
                fullWidth
              />
              <TextField
                label="Code"
                value={createForm.code}
                onChange={handleCreateFieldChange('code')}
                error={Boolean(createFormErrors.code)}
                helperText={createFormErrors.code || 'Short identifier used in reporting'}
                fullWidth
              />
              <TextField
                label="Slug"
                value={createForm.slug}
                onChange={handleCreateFieldChange('slug')}
                error={Boolean(createFormErrors.slug)}
                helperText={createFormErrors.slug || 'Optional: lower-case URL fragment (letters, numbers, hyphen)'}
                fullWidth
              />
              <TextField
                select
                label="Status"
                value={createForm.status}
                onChange={handleCreateFieldChange('status')}
                fullWidth
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
              <TextField
                label="Seat capacity"
                type="number"
                value={createForm.seatCapacity}
                onChange={handleCreateFieldChange('seatCapacity')}
                error={Boolean(createFormErrors.seatCapacity)}
                helperText={createFormErrors.seatCapacity || 'How many active learners are permitted'}
                inputProps={{ min: 0 }}
                fullWidth
              />
              <TextField
                label="Contact email"
                value={createForm.contactEmail}
                onChange={handleCreateFieldChange('contactEmail')}
                error={Boolean(createFormErrors.contactEmail)}
                helperText={createFormErrors.contactEmail || 'Primary contact for notifications'}
                fullWidth
              />
              <TextField
                label="Contact phone"
                value={createForm.contactPhone}
                onChange={handleCreateFieldChange('contactPhone')}
                fullWidth
              />
              <Typography variant="caption" color="text.secondary">
                New universities are bootstrapped with default presenting complaints and specialties unique to their tenant.
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeCreateUniversityDialog} disabled={creatingUniversity}>Cancel</Button>
            <Button
              variant="contained"
              onClick={submitCreateUniversity}
              disabled={creatingUniversity || !isCreateFormValid}
            >
              {creatingUniversity ? 'Creating...' : 'Create university'}
            </Button>
          </DialogActions>
        </Dialog>
        <Snackbar
          open={toast.open}
          autoHideDuration={5000}
          onClose={handleToastClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleToastClose}
            severity={toast.severity}
            sx={{ width: '100%' }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  };

export default GlobalAdminConsole;
